import React, { useState, useMemo, useEffect } from 'react';
import { usePersistedState, useStableKeyId } from '@/hooks/usePersistedState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import ChannelSummaryReport from './channel/ChannelSummaryReport';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CalendarIcon, FileText, Bell, TrendingUp, TrendingDown, Target, Users, 
  DollarSign, Zap, MousePointer, Eye, BarChart3, PieChart, Percent,
  Radio, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, Lightbulb, X,
  Activity, Send, FileEdit, Award, Download, Printer, Share2, UserCheck, MessageCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import { format, startOfMonth, startOfWeek, endOfWeek, addDays, subDays } from 'date-fns';
import { TrafficLightIndicator } from '@/components/TrafficLightIndicator';
import { PerformanceTracker } from '@/components/shared/PerformanceTracker';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import KpiHelpButton, { CONVERSION_RATE_KPIS, OPS_CONVERSION_KPIS, PIPELINE_STUCK_KPIS } from '@/components/KpiHelpButton';
import axios from 'axios';
import { safeNum, safeInt, sumLeads, sumClosed, sumCommissionMyr, sumDepositMyr, sumDepositUsd, sumCommissionUsd } from '@/utils/dataHelpers';
import { useExchangeRate, toYearMonth } from '@/hooks/useExchangeRate';
import RateInfoBadge from '@/components/RateInfoBadge';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================
// OVERVIEW PERFORMANCE PROJECTIONS COMPONENT
// ============================================
function OverviewProjections({ activeData, hasRealData, usdToMyr, kpiTargets, monthlyTargets = {}, prevMonthData = {}, calcTrend: calcTrendFn }) {
  const [scenario, setScenario] = useState('base');
  const [customPercent, setCustomPercent] = useState(10);
  const [adjustment, setAdjustment] = useState(0);
  
  // All scenario definitions
  const allScenarios = {
    base: { label: 'Base Scenario', multiplier: 1.0, isConversion: false },
    cta_conv: { label: 'CTA Conversion', multiplier: 1.0, isConversion: true, conversionType: 'cta', description: 'clicks → leads' },
    subs_conv: { label: 'Subscriber Conversion', multiplier: 1.0, isConversion: true, conversionType: 'subs', description: 'views → subs' },
    lead_conv: { label: 'Lead Conversion', multiplier: 1.0, isConversion: true, conversionType: 'lead', description: 'subs → leads' },
    close_conv: { label: 'Closing Conversion', multiplier: 1.0, isConversion: true, conversionType: 'close', description: 'leads → closed' },
    aggressive: { label: 'Aggressive +50%', multiplier: 1.5, isConversion: false },
  };
  
  const currentScenario = allScenarios[scenario] || allScenarios.base;
  const isConversionScenario = currentScenario.isConversion;
  
  // Calculate days remaining
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const daysRemaining = Math.max(0, lastDay.getDate() - now.getDate());
  const currentDay = now.getDate();
  const daysInMonth = lastDay.getDate();
  
  // Get target from kpiTargets (for KPI-based metrics)
  const getTarget = (dept, key) => kpiTargets[dept]?.[key] || 0;
  
  // Get target from monthlyTargets (for Profile-based metrics)
  const getMonthlyTarget = (key) => monthlyTargets[key] || 0;
  
  // Use prevMonthData prop passed from parent
  const prevMonthOverview = prevMonthData || {};
  const calcTrend = calcTrendFn || ((c, p) => { if(!p||p===0) return c>0?100:0; return ((c-p)/p*100); });

  // Projection metrics - using Monthly Targets from Channel Settings
  const projectionMetrics = [
    { key: 'totalSpend', label: 'Ad Spend', current: activeData?.total_ad_spent || 0, target: getTarget('marketing', 'totalSpend'), dailyAvg: (activeData?.total_ad_spent || 0) / Math.max(currentDay, 1), icon: DollarSign, color: '#E63946', prefix: 'RM ', trend: calcTrend(activeData?.total_ad_spent||0, prevMonthOverview.total_ad_spent) },
    { key: 'subscribers', label: 'Subscribers', current: activeData?.total_subscribers || 0, target: getMonthlyTarget('subscribers'), dailyAvg: (activeData?.total_subscribers || 0) / Math.max(currentDay, 1), icon: Users, color: '#3B82F6', conversionType: 'subs', trend: calcTrend(activeData?.total_subscribers||0, prevMonthOverview.total_subscribers) },
    { key: 'leads', label: 'Leads', current: activeData?.total_leads || 0, target: getMonthlyTarget('leads'), dailyAvg: (activeData?.total_leads || 0) / Math.max(currentDay, 1), icon: Target, color: '#10B981', conversionType: 'lead', trend: calcTrend(activeData?.total_leads||0, prevMonthOverview.total_leads) },
    { key: 'closed', label: 'Closed', current: activeData?.total_closing || 0, target: getMonthlyTarget('closed'), dailyAvg: (activeData?.total_closing || 0) / Math.max(currentDay, 1), icon: Zap, color: '#F59E0B', conversionType: 'close', trend: calcTrend(activeData?.total_closing||0, prevMonthOverview.total_closing) },
    { key: 'deposit', label: 'Deposit', current: activeData?.total_deposit || 0, target: getMonthlyTarget('deposit'), dailyAvg: (activeData?.total_deposit || 0) / Math.max(currentDay, 1), icon: DollarSign, color: '#8B5CF6', prefix: '$', trend: calcTrend(activeData?.total_deposit||0, prevMonthOverview.total_deposit) },
    { key: 'commission', label: 'Commission', current: activeData?.total_commission || 0, target: getMonthlyTarget('commission'), dailyAvg: (activeData?.total_commission || 0) / Math.max(currentDay, 1), icon: TrendingUp, color: '#F97316', prefix: 'RM ', trend: calcTrend(activeData?.total_commission||0, prevMonthOverview.total_commission) },
  ];
  
  // Calculate projection
  const getProjection = (item) => {
    let scenarioFactor = 1.0;
    
    if (isConversionScenario && item.conversionType === currentScenario.conversionType) {
      scenarioFactor = 1 + (customPercent / 100);
    } else if (!isConversionScenario) {
      scenarioFactor = currentScenario.multiplier;
    }
    
    const adjustmentFactor = 1 + (adjustment / 100);
    const totalFactor = scenarioFactor * adjustmentFactor;
    
    // Guard against NaN
    const current = item.current || 0;
    const dailyAvg = item.dailyAvg || 0;
    const target = item.target || 0;
    
    const projected = current + (dailyAvg * daysRemaining * totalFactor);
    const projectedSafe = isNaN(projected) ? 0 : projected;
    
    const percentComplete = target > 0 ? ((projectedSafe / target) * 100).toFixed(1) : (projectedSafe > 0 ? '—' : '0');
    const gap = target > 0 ? target - projectedSafe : 0;
    
    return { projected: projectedSafe, percentComplete, gap, isOver: gap <= 0, hasTarget: target > 0 };
  };
  
  const getColor = (percent) => {
    const p = parseFloat(percent);
    if (isNaN(p) || percent === '—') return '#6B7280'; // Gray for no data
    if (p >= 100) return '#3B82F6';
    if (p >= 80) return '#10B981';
    if (p >= 50) return '#F59E0B';
    return '#E63946';
  };
  
  return (
    <div className="relative rounded-xl overflow-hidden mb-6" style={{
      background: 'linear-gradient(135deg, rgba(230,57,70,0.08) 0%, rgba(249,115,22,0.06) 50%, rgba(230,57,70,0.04) 100%)',
      border: '1px solid rgba(230,57,70,0.3)',
      boxShadow: '0 0 40px -10px rgba(230,57,70,0.4)'
    }}>
      <SectionHeader icon={TrendingUp} title="Performance Projections" subtitle={`End of Month Forecast • ${daysRemaining} days remaining`} />
      
      <div className="p-5">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Select value={scenario} onValueChange={setScenario}>
            <SelectTrigger className="w-52 h-9 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
              {Object.entries(allScenarios).map(([key, val]) => (
                <SelectItem key={key} value={key} className="text-white text-sm">
                  {val.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isConversionScenario && (
            <div className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-3 py-1">
              <span className="text-xs text-[#9CA3AF]">+</span>
              <Input
                type="number"
                min="1"
                max="100"
                value={customPercent}
                onChange={(e) => setCustomPercent(Math.min(100, Math.max(1, parseInt(e.target.value) || 10)))}
                className="w-14 h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-mono text-sm text-center"
              />
              <span className="text-xs text-[#9CA3AF]">%</span>
              <span className="text-[10px] text-[#6B7280]">({currentScenario.description})</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.max(-50, adjustment - 5))} className="h-8 border-[hsl(var(--border))] text-white hover:bg-white/5">-5%</Button>
            <span className="text-xs font-mono text-white w-12 text-center bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1">
              {adjustment > 0 ? '+' : ''}{adjustment}%
            </span>
            <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.min(50, adjustment + 5))} className="h-8 border-[hsl(var(--border))] text-white hover:bg-white/5">+5%</Button>
          </div>
        </div>
        
        {/* Projection Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {projectionMetrics.map((item) => {
            const Icon = item.icon;
            const proj = getProjection(item);
            const color = getColor(proj.percentComplete);
            const hasConversionApplied = isConversionScenario && item.conversionType === currentScenario.conversionType;
            const hasData = item.current > 0 || item.target > 0;
            
            return (
              <div key={item.key} className="relative">
                {hasConversionApplied && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#10B981] flex items-center justify-center z-10">
                    <TrendingUp size={10} className="text-white" />
                  </div>
                )}
                <div 
                  className="rounded-xl p-3 h-full border transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: hasData ? `${color}10` : 'rgba(107, 114, 128, 0.06)', 
                    borderColor: hasConversionApplied ? '#10B981' : (hasData ? `${color}30` : 'rgba(107, 114, 128, 0.25)'),
                    boxShadow: hasData ? `0 0 20px -5px ${item.color}40` : 'none'
                  }}
                >
                  <div className="flex justify-center mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                      <Icon size={14} style={{ color: item.color }} />
                    </div>
                  </div>
                  <p className="text-[10px] font-medium text-[#9CA3AF] mb-2 text-center">Projected {item.label}</p>
                  <p className="text-lg font-mono font-bold text-white leading-none mb-1 text-center">
                    {item.prefix || ''}{Math.round(proj.projected).toLocaleString()}
                  </p>
                  {item.trend !== undefined && item.trend !== 0 && (
                    <p className={`text-[10px] font-medium text-center ${item.trend >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                      {item.trend >= 0 ? '+' : ''}{item.trend.toFixed(1)}% vs last month
                    </p>
                  )}
                  {hasData && proj.hasTarget && (
                    <>
                      <p className="text-sm font-mono font-bold text-center" style={{ color }}>
                        {proj.percentComplete}%
                      </p>
                      <p className="text-[9px] text-center" style={{ color }}>
                        {proj.isOver ? `+${Math.abs(Math.round(proj.gap)).toLocaleString()}` : `-${Math.round(proj.gap).toLocaleString()}`}
                      </p>
                    </>
                  )}
                  {hasData && !proj.hasTarget && (
                    <p className="text-xs text-[#6B7280] text-center">No target set</p>
                  )}
                  <div className="grid grid-cols-2 gap-1 pt-2 mt-2 border-t border-white/10">
                    <div className="text-center">
                      <p className="text-[8px] uppercase text-[#6B7280]">Now</p>
                      <p className="text-[10px] font-mono text-white">{item.prefix || ''}{Math.round(item.current).toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[8px] uppercase text-[#6B7280]">Target</p>
                      <p className="text-[10px] font-mono text-[#9CA3AF]">{item.prefix || ''}{Math.round(item.target).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * CHANNEL OVERVIEW - LINEAR-MODERN + LUXURY DESIGN
 * Redesigned with standardized headers matching Channel Profile
 */

// Standardized Section Header Component
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
        {action && <div className="ml-auto flex items-center">{action}</div>}
      </div>
    </div>
  );
}

export const ChannelOverviewContent = ({ ib }) => {
  // Check if this IB has real data (not just mock)
  const hasRealData = true; // All IBs now have imported data from Google Sheets
  
  // Default date range: 1st of current month to today
  const [currentDate] = useState(() => new Date());
  const defaultStartDate = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const defaultEndDate = currentDate;
  
  // USD to MYR exchange rate — admin-set per month (source of truth).
  // Fallback to external live rate only if admin hasn't configured the month.
  const { getRateForDate, defaultRate: adminDefaultRate } = useExchangeRate();
  const [usdToMyr, setUsdToMyr] = useState(() => {
    const saved = localStorage.getItem('usd_myr_rate');
    return saved ? parseFloat(saved) : 4.2; // Default fallback
  });

  // Prefer admin-set rate over external live rate once loaded.
  useEffect(() => {
    if (adminDefaultRate) setUsdToMyr(adminDefaultRate);
  }, [adminDefaultRate]);

  // External live rate fallback (kept for display only — aggregations use
  // per-row admin rate via getRateForDate).
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data.rates && data.rates.MYR) {
          const rate = data.rates.MYR;
          // Only overwrite if admin hasn't set one
          if (!adminDefaultRate) setUsdToMyr(rate);
          localStorage.setItem('usd_myr_rate', rate.toString());
          localStorage.setItem('usd_myr_rate_updated', new Date().toISOString());
        }
      } catch (error) {
        console.log('Using cached exchange rate:', usdToMyr);
      }
    };

    const lastUpdated = localStorage.getItem('usd_myr_rate_updated');
    const isStale = !lastUpdated || (Date.now() - new Date(lastUpdated).getTime()) > 3600000;

    if (isStale) {
      fetchExchangeRate();
    }
  }, [adminDefaultRate]);
  
  // Check if there's data for current month in localStorage
  const hasCurrentMonthData = useMemo(() => {
    const savedMarketing = localStorage.getItem(`marketing_data_${ib?.id || 'default'}`);
    const savedSales = localStorage.getItem(`sales_data_${ib?.id || 'default'}`);
    
    if (!savedMarketing && !savedSales) return false;
    
    const marketingData = savedMarketing ? JSON.parse(savedMarketing) : [];
    const salesData = savedSales ? JSON.parse(savedSales) : [];
    
    // Check if any data is in current month
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    const hasMarketingData = marketingData.some(d => {
      const date = new Date(d.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    const hasSalesData = salesData.some(d => {
      const date = new Date(d.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    return hasMarketingData || hasSalesData;
  }, [ib?.id, currentDate]);
  
  // Smart default date range - check if current month has data
  const getDefaultDateRange = () => {
    if (hasCurrentMonthData) return 'mtd';
    // If no current month data, default to full (all time)
    return 'full';
  };
  
  const OV_P = `channel-overview:${useStableKeyId(ib?.id)}`;
  const [dateRange, setDateRange] = usePersistedState(`${OV_P}:dateRange`, getDefaultDateRange());
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [editMeta, setEditMeta] = useState({});
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [scenario, setScenario] = useState('base');
  const [adjustment, setAdjustment] = useState(0);
  const [forecastPeriod, setForecastPeriod] = usePersistedState(`${OV_P}:forecast`, 'month');
  
  // KPI Targets from Channel Settings
  const [kpiTargets, setKpiTargets] = useState({
    marketing: {},
    sales: {},
    operations: {}
  });
  
  // Monthly Targets from Channel Settings (for Profile display)
  const [monthlyTargets, setMonthlyTargets] = useState({
    subscribers: 0, leads: 0, closed: 0, deposit: 0, activeClient: 0, tradedLot: 0, commission: 0
  });
  
  // Fetch KPI targets and Monthly Targets from API
  useEffect(() => {
    const fetchTargets = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !ib?.id) return;
        
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        
        // Only log once - avoid repeated console logs
        // console.log('[KPI Targets] Fetching for:', ib.id, currentYear, currentMonth);
        
        // Fetch KPI Targets
        const kpiResponse = await axios.get(
          `${API}/api/kpi-targets/${ib.id}/${currentYear}/${currentMonth}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (kpiResponse.data) {
          setKpiTargets(kpiResponse.data);
        }
        
        // Fetch Monthly Targets (Channel Targets)
        const monthlyResponse = await axios.get(
          `${API}/api/channel-targets/${ib.id}/${currentYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (monthlyResponse.data?.monthly) {
          const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
          const targets = monthlyResponse.data.monthly[monthKey] || {};
          setMonthlyTargets(prev => {
            // Only update if values changed to prevent re-renders
            const prevStr = JSON.stringify(prev);
            const newStr = JSON.stringify({...prev, ...targets});
            if (prevStr === newStr) return prev;
            return {...prev, ...targets};
          });
        }
      } catch (error) {
        console.log('[Targets] Error:', error.message);
        // Fallback to MongoDB generic store
        try {
          const fbToken = localStorage.getItem('token');
          const kpiRes = await axios.get(`${API}/api/ib-data/${ib?.id}/kpi_targets`, { headers: { Authorization: `Bearer ${fbToken}` } }).catch(() => ({ data: { data: null } }));
          if (kpiRes.data?.data) {
            const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            setKpiTargets(kpiRes.data.data[monthKey] || {});
          }
          const monthlyRes = await axios.get(`${API}/api/ib-data/${ib?.id}/monthly_targets`, { headers: { Authorization: `Bearer ${fbToken}` } }).catch(() => ({ data: { data: null } }));
          if (monthlyRes.data?.data) {
            const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            setMonthlyTargets(prev => ({...prev, ...(monthlyRes.data.data[monthKey] || {})}));
          }
        } catch { /* ignore */ }
      }
    };
    
    fetchTargets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ib?.id]);
  
  // Summary Modal state — legacy weekly/monthly state removed; the new
  // ChannelSummaryReport component owns its own period picker.
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  
  // Active brokers from MongoDB/IB data
  const [activeBrokers, setActiveBrokers] = useState(['HFM', 'OCTA', 'VANTAGE']);
  
  React.useEffect(() => {
    if (!ib?.id) return;
    const fetchBrokerConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/ib-data/${ib.id}/broker_config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.brokerConfig) {
          const config = res.data.data.brokerConfig;
          const names = Object.values(config).filter(b => b.enabled && b.name).map(b => b.name.toUpperCase());
          if (names.length > 0) setActiveBrokers(names);
        }
        if (res.data?.updated_by_name) {
          setEditMeta({ name: res.data.updated_by_name, time: res.data.updated_at });
        }
      } catch { /* ignore */ }
    };
    fetchBrokerConfig();
  }, [ib?.id]);

  // Calculate dateFrom and dateTo based on dateRange (using useMemo to avoid cascading renders)
  const { dateFrom, dateTo } = React.useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    switch(dateRange) {
      case 'today':
        return { dateFrom: today, dateTo: endOfToday };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return { dateFrom: yesterday, dateTo: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59) };
      case 'this-week':
        return { dateFrom: startOfWeek(now, { weekStartsOn: 1 }), dateTo: endOfToday };
      case 'last-week':
        const lastWeekStart = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
        return { dateFrom: lastWeekStart, dateTo: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
      case 'this-month':
      case 'mtd':
        return { dateFrom: startOfMonth(now), dateTo: endOfToday };
      case 'last-month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { dateFrom: lastMonth, dateTo: lastDayOfPrevMonth };
      case 'full':
        // Full maximum - combine 2025 Google Sheet data + 2026 Daily Data
        // Start from 2025-01-01 (earliest possible) to latest available date
        const savedMarketingFull = localStorage.getItem(`marketing_data_${ib?.id || 'default'}`);
        const savedSalesFull = localStorage.getItem(`sales_data_${ib?.id || 'default'}`);
        
        // Default: start from 2025-01-01 (Google Sheet has 2025 data)
        let earliestDateFull = new Date('2025-01-01');
        let latestDateFull = now;
        
        // Check localStorage for the latest date (2026 data)
        if (savedMarketingFull || savedSalesFull) {
          const marketingDatesFull = savedMarketingFull ? JSON.parse(savedMarketingFull).map(d => new Date(d.date)) : [];
          const salesDatesFull = savedSalesFull ? JSON.parse(savedSalesFull).map(d => new Date(d.date)) : [];
          const allDatesFull = [...marketingDatesFull, ...salesDatesFull].filter(d => !isNaN(d.getTime()));
          if (allDatesFull.length > 0) {
            // Keep earliest as 2025-01-01 to include Google Sheet data
            // Latest is max of localStorage data and current date
            latestDateFull = new Date(Math.max(...allDatesFull.map(d => d.getTime()), now.getTime()));
          }
        }
        return { dateFrom: earliestDateFull, dateTo: latestDateFull };
      case 'custom':
        return { 
          dateFrom: customDateFrom || defaultStartDate, 
          dateTo: customDateTo || defaultEndDate 
        };
      // 2025 Historical ranges (from imported Google Sheet)
      case '2025-full':
        return { dateFrom: new Date('2025-01-01'), dateTo: new Date('2025-12-31') };
      case '2025-q1':
        return { dateFrom: new Date('2025-01-01'), dateTo: new Date('2025-03-31') };
      case '2025-q2':
        return { dateFrom: new Date('2025-04-01'), dateTo: new Date('2025-06-30') };
      case '2025-q3':
        return { dateFrom: new Date('2025-07-01'), dateTo: new Date('2025-09-30') };
      case '2025-q4':
        return { dateFrom: new Date('2025-10-01'), dateTo: new Date('2025-12-31') };
      default:
        return { dateFrom: defaultStartDate, dateTo: defaultEndDate };
    }
  }, [dateRange, customDateFrom, customDateTo, defaultStartDate, defaultEndDate]);

  // Weekday count in current date range — used for "avg per weekday" displays.
  const weekdayCount = React.useMemo(() => {
    if (!dateFrom || !dateTo) return 0;
    const cur = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
    const end = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
    let n = 0;
    while (cur <= end) {
      const dow = cur.getDay();
      if (dow >= 1 && dow <= 5) n += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return n;
  }, [dateFrom, dateTo]);

  // Load broker config from MongoDB
  const [brokerConfig, setBrokerConfig] = useState({
    brokerA: { enabled: true, name: 'HFM' },
    brokerB: { enabled: true, name: 'OCTA' },
    brokerC: { enabled: true, name: 'VANTAGE' },
    brokerD: { enabled: false, name: '' },
    brokerE: { enabled: false, name: '' }
  });
  const [kpiThresholds, setKpiThresholds] = useState(null);
  useEffect(() => {
    const loadKpi = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/admin/kpi-thresholds`, { headers: { Authorization: `Bearer ${token}` } });
        setKpiThresholds(res.data.thresholds);
      } catch { /* ignore */ }
    };
    loadKpi();
  }, []);
  
  // Update broker config from IB's active_brokers or MongoDB
  useEffect(() => {
    const loadBrokerConfig = async () => {
      // First try MongoDB
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/ib-data/${ib?.id}/broker_config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.brokerConfig) {
          setBrokerConfig(res.data.data.brokerConfig);
          return;
        }
      } catch { /* ignore */ }
      
      // Fallback to IB active_brokers
      if (ib?.active_brokers && ib.active_brokers.length > 0) {
        const brokerNameMap = { 'octa': 'Octa', 'hfm': 'HFM', 'vantage': 'Vantage', 'pepperstone': 'Pepperstone', 'moneta': 'Moneta', 'valetax': 'Valetax' };
        const newBrokerConfig = {
          brokerA: { name: '', enabled: false }, brokerB: { name: '', enabled: false },
          brokerC: { name: '', enabled: false }, brokerD: { name: '', enabled: false }, brokerE: { name: '', enabled: false },
        };
        const brokerKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
        ib.active_brokers.forEach((broker, index) => {
          if (index < 5) newBrokerConfig[brokerKeys[index]] = { name: brokerNameMap[broker] || broker, enabled: true };
        });
        setBrokerConfig(newBrokerConfig);
      }
    };
    if (ib?.id) loadBrokerConfig();
  }, [ib?.id, ib?.active_brokers]);
  
  // Listen for broker config changes (from other pages)
  useEffect(() => {
    const handleBrokerConfigChange = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/ib-data/${ib?.id}/broker_config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.brokerConfig) setBrokerConfig(res.data.data.brokerConfig);
      } catch { /* ignore */ }
    };
    window.addEventListener('brokerConfigUpdated', handleBrokerConfigChange);
    return () => window.removeEventListener('brokerConfigUpdated', handleBrokerConfigChange);
  }, [ib?.id]);

  // Data states
  const [historicalData, setHistoricalData] = useState(null);
  const [dailyData, setDailyData] = useState({ marketing: [], sales: [] });
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  
  // Is this historical data (2025)?
  const isHistorical = dateRange?.startsWith('2025') && historicalData;
  
  // Load Daily Data from API (all months)
  useEffect(() => {
    const loadDailyData = async () => {
      if (!ib?.id) return;
      
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      try {
        // Fetch ALL relevant months (supports any year going forward)
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        
        const relevantMonths = [];
        for (let y = 2026; y <= currentYear; y++) {
          const maxMonth = y === currentYear ? currentMonth : 12;
          for (let m = 1; m <= maxMonth; m++) {
            relevantMonths.push(`${y}-${String(m).padStart(2, '0')}`);
          }
        }
        
        let allMarketing = [];
        let allSales = [];
        
        const promises = relevantMonths.map(month =>
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/historical/2026/daily-data/${ib.id}?month=${month}`,
            { headers }
          ).catch(() => null)
        );
        const results = await Promise.all(promises);
        
        for (const res of results) {
          if (res?.data) {
            if (res.data.marketing?.length) allMarketing = [...allMarketing, ...res.data.marketing];
            if (res.data.sales?.length) allSales = [...allSales, ...res.data.sales];
          }
        }

        // Defensive dedup by date (YYYY-MM-DD normalized) — prevents inflated totals
        // if backend ever forgets to dedup (belt-and-braces with backend fix).
        const dedupByDate = (arr) => {
          const seen = new Set();
          const out = [];
          for (const item of arr) {
            if (!item || typeof item !== 'object') continue;
            const d = String(item.date || '').trim().slice(0, 10);
            if (d && !seen.has(d)) {
              seen.add(d);
              out.push(item);
            }
          }
          return out;
        };
        allMarketing = dedupByDate(allMarketing);
        allSales = dedupByDate(allSales);

        if (allMarketing.length > 0 || allSales.length > 0) {
          setDailyData({ marketing: allMarketing, sales: allSales });
          return;
        }
      } catch (error) {
        console.log('API fetch failed, falling back to localStorage');
      }
      
      // Fallback to localStorage only if API fails or returns empty
      const savedMarketing = localStorage.getItem(`marketing_data_${ib.id}`);
      const savedSales = localStorage.getItem(`sales_data_${ib.id}`);
      
      setDailyData({
        marketing: savedMarketing ? JSON.parse(savedMarketing) : [],
        sales: savedSales ? JSON.parse(savedSales) : []
      });
    };
    
    loadDailyData();
    
    // Listen for storage changes (when Daily Data page updates - cross-tab)
    const handleStorageChange = (e) => {
      if (e.key === `marketing_data_${ib?.id}` || e.key === `sales_data_${ib?.id}`) {
        // Reload from localStorage for this specific IB
        const savedMarketing = localStorage.getItem(`marketing_data_${ib.id}`);
        const savedSales = localStorage.getItem(`sales_data_${ib.id}`);
        setDailyData({
          marketing: savedMarketing ? JSON.parse(savedMarketing) : [],
          sales: savedSales ? JSON.parse(savedSales) : []
        });
      }
    };
    
    // Listen for custom event (same-tab updates from DailyDataContent)
    const handleDailyDataUpdated = (e) => {
      if (e.detail?.ibId === ib?.id) {
        loadDailyData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('dailyDataUpdated', handleDailyDataUpdated);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dailyDataUpdated', handleDailyDataUpdated);
    };
  }, [ib?.id]);
  
  // Aggregate Daily Data based on date range
  const aggregatedDailyData = useMemo(() => {
    if (!dailyData.marketing?.length && !dailyData.sales?.length) return null;
    
    // Filter by date range
    const filterByDateRange = (data) => {
      if (!data?.length) return [];
      return data.filter(item => {
        if (!item.date) return false;
        const itemDate = new Date(item.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
    };
    
    const filteredMarketing = filterByDateRange(dailyData.marketing);
    const filteredSales = filterByDateRange(dailyData.sales);
    
    // Helper: best value for a broker field, reading BOTH storage shapes but
    // NEVER adding them together — old imports merged two brokers into one dict
    // key (dict `vantage` = vantage + xm), so mixing shapes double-counted money.
    // Mirrors backend utils/data_helpers._resolve_total.
    const enabledKeys = Object.entries(brokerConfig).filter(([, v]) => v.enabled).map(([k]) => k);
    const enabledIds = new Set([
      ...enabledKeys.map((k) => k.toLowerCase()),
      ...enabledKeys.map((k) => String(brokerConfig?.[k]?.name || '').toLowerCase().trim()).filter(Boolean),
    ]);
    const brokerFieldValue = (bd, field) => {
      let v;
      if (field === 'commission') v = bd.commission_usd ?? bd.commission;
      else if (field === 'deposit') v = bd.deposit_usd ?? bd.deposit;
      else v = bd[field];
      return safeNum(v || 0);
    };
    const sumEnabledBrokerField = (row, field) => {
      if (!row || typeof row !== 'object') return 0;

      const hasDict = row.brokers !== undefined && typeof row.brokers === 'object' && row.brokers !== null;
      let dictTotal = 0;
      let dictCount = 0;
      if (hasDict) {
        for (const [k, bd] of Object.entries(row.brokers)) {
          if (!bd || typeof bd !== 'object') continue;
          if (enabledIds.size > 0) {
            const key = String(k).toLowerCase().trim();
            const name = String(bd.name || '').toLowerCase().trim();
            if (!enabledIds.has(key) && !enabledIds.has(name)) continue;
          }
          const v = brokerFieldValue(bd, field);
          dictTotal += v;
          if (v) dictCount += 1;
        }
      }

      let legacy = 0;
      let legacyCount = 0;
      const keys = enabledKeys.length > 0 ? enabledKeys : ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
      for (const key of keys) {
        const bd = row[key];
        if (bd && typeof bd === 'object') {
          const v = brokerFieldValue(bd, field);
          legacy += v;
          if (v) legacyCount += 1;
        }
      }

      // The name-keyed dict wins whenever it covers at least as many brokers as
      // the legacy slots: production had one legacy slot copying another
      // broker's numbers, which double-counted that broker in the totals.
      const best = hasDict && dictCount >= legacyCount ? dictTotal : Math.max(dictTotal, legacy);
      if (best > 0 || hasDict || legacy > 0) return best;

      if (field === 'commission') return safeNum(row.total_commission_usd);
      if (field === 'deposit') return safeNum(row.total_deposit_usd);
      if (field === 'active') return safeNum(row.total_active_client);
      if (field === 'lots') return safeNum(row.total_traded_lot);
      return 0;
    };
    
    if (!filteredMarketing.length && !filteredSales.length) return null;
    
    // Aggregate
    return {
      total_ad_spent: filteredMarketing.reduce((sum, d) => sum + safeNum(d.spend), 0),
      total_impressions: filteredMarketing.reduce((sum, d) => sum + safeNum(d.impressions), 0),
      total_clicks: filteredMarketing.reduce((sum, d) => sum + safeNum(d.clicks) + safeInt(d.cta), 0),
      total_lpv: filteredMarketing.reduce((sum, d) => sum + safeInt(d.lpv), 0),
      total_cta: filteredMarketing.reduce((sum, d) => sum + safeInt(d.cta), 0),
      total_subscribers: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsTG) + safeInt(d.subsBot), 0),
      total_subs_tg: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsTG), 0),
      total_subs_bot: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsBot), 0),
      total_subs_free_trial: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsFreeTrial), 0),
      total_leads_m: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsM), 0),
      total_leads_s: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsS), 0),
      total_leads_ib: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsIB), 0),
      total_leads: filteredSales.reduce((sum, d) => sum + sumLeads(d), 0),
      total_closing: filteredSales.reduce((sum, d) => sum + sumClosed(d), 0),
      total_active_client: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'active'), 0),
      total_traded_lot: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'lots'), 0),
      // Commission MYR = per-row USD × admin-set rate for the row's month
      // (single source of truth with scorecard backend).
      total_commission: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'commission') * getRateForDate(d.date), 0),
      total_commission_usd: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'commission'), 0),
      total_deposit: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'deposit'), 0),
      // Sales funnel data
      greeting: filteredSales.reduce((sum, d) => sum + (d.greeting || 0), 0),
      oneTick: filteredSales.reduce((sum, d) => sum + (d.oneTick || 0), 0),
      registration: filteredSales.reduce((sum, d) => sum + (d.registration || 0), 0),
      broker: filteredSales.reduce((sum, d) => sum + (d.broker || 0), 0),
      priceOffer: filteredSales.reduce((sum, d) => sum + (d.priceOffer || 0), 0),
      record_count: filteredMarketing.length + filteredSales.length
    };
  }, [dailyData, dateFrom, dateTo, brokerConfig, getRateForDate]);

  // Fetch historical data (from backend API)
  // - 2025: From Google Sheet import
  // - 2026: From Daily Data + MTProto stats
  useEffect(() => {
    const fetchHistoricalData = async () => {
      if (!ib?.id) return;
      
      const startYear = dateFrom?.getFullYear();
      const endYear = dateTo?.getFullYear();
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Always reset historicalData when IB changes to prevent stale data
      setHistoricalData(null);
      
      // For 2026-only ranges: Just set MTProto data (daily data is loaded by separate useEffect)
      if ((startYear === 2026 && endYear === 2026) || dateRange === 'mtd' || dateRange === 'this-month' || dateRange === 'last-month' || dateRange === 'this-week' || dateRange === 'last-week' || dateRange === 'today' || dateRange === 'yesterday') {
        // Daily data is already loaded by loadDailyData useEffect
        // Just clear historical data since we don't need 2025 summary
        setHistoricalData(null);
        return;
      }
      
      // Fetch 2025 data from backend API when:
      // 1. Date range includes 2025, OR
      // 2. "Full Maximum" is selected (which starts from 2025-01-01)
      const needsHistoricalData = startYear === 2025 || endYear === 2025 || 
                                   dateRange?.startsWith('2025') || dateRange === 'full';
      
      if (needsHistoricalData) {
        setIsLoadingHistorical(true);
        try {
          // For "full" range, always fetch from 2025-01-01 to 2025-12-31
          const apiStartDate = dateRange === 'full' ? '2025-01-01' : format(dateFrom, 'yyyy-MM-dd');
          const apiEndDate = dateRange === 'full' ? '2025-12-31' : format(dateTo, 'yyyy-MM-dd');
          
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/historical/2025/${ib.id}/summary`,
            { 
              params: { start_date: apiStartDate, end_date: apiEndDate },
              headers
            }
          );
          setHistoricalData(response.data);
        } catch (error) {
          console.error('Failed to fetch historical data:', error);
          setHistoricalData(null);
        } finally {
          setIsLoadingHistorical(false);
        }
      } else {
        setHistoricalData(null);
      }
    };
    
    fetchHistoricalData();
  }, [ib?.id, dateFrom, dateTo, dateRange]);
  
  // Combined data source - For "Full Maximum", COMBINE historical 2025 + localStorage 2026 data
  const activeData = useMemo(() => {
    // Full Maximum: combine both 2025 API data and 2026 localStorage data
    if (dateRange === 'full') {
      const combined = {
        // Start with 2025 historical data (from API)
        total_ad_spent: (historicalData?.total_ad_spent || 0) + (aggregatedDailyData?.total_ad_spent || 0),
        total_impressions: (historicalData?.total_impressions || 0) + (aggregatedDailyData?.total_impressions || 0),
        total_clicks: (historicalData?.total_clicks || 0) + (aggregatedDailyData?.total_clicks || 0),
        total_lpv: (historicalData?.total_lpv || 0) + (aggregatedDailyData?.total_lpv || 0),
        total_cta: (historicalData?.total_cta || 0) + (aggregatedDailyData?.total_cta || 0),
        total_subscribers: (historicalData?.total_subscribers || 0) + (aggregatedDailyData?.total_subscribers || 0),
        total_subs_tg: (historicalData?.total_subs_tg || 0) + (aggregatedDailyData?.total_subs_tg || 0),
        total_subs_bot: (historicalData?.total_subs_bot || 0) + (aggregatedDailyData?.total_subs_bot || 0),
        total_subs_free_trial: (historicalData?.total_subs_free_trial || 0) + (aggregatedDailyData?.total_subs_free_trial || 0),
        total_leads: (historicalData?.total_leads || 0) + (aggregatedDailyData?.total_leads || 0),
        total_closing: (historicalData?.total_closing || 0) + (aggregatedDailyData?.total_closing || 0),
        total_active_client: (historicalData?.total_active_client || 0) + (aggregatedDailyData?.total_active_client || 0),
        total_traded_lot: (historicalData?.total_traded_lot || 0) + (aggregatedDailyData?.total_traded_lot || 0),
        total_commission: (historicalData?.total_commission || 0) + (aggregatedDailyData?.total_commission || 0),
        total_commission_usd: (historicalData?.total_commission_usd || 0) + (aggregatedDailyData?.total_commission_usd || 0),
        total_deposit: (historicalData?.total_deposit || 0) + (aggregatedDailyData?.total_deposit || 0),
        greeting: (historicalData?.greeting || 0) + (aggregatedDailyData?.greeting || 0),
        oneTick: (historicalData?.oneTick || 0) + (aggregatedDailyData?.oneTick || 0),
        registration: (historicalData?.registration || 0) + (aggregatedDailyData?.registration || 0),
        broker: (historicalData?.broker || 0) + (aggregatedDailyData?.broker || 0),
        priceOffer: (historicalData?.priceOffer || 0) + (aggregatedDailyData?.priceOffer || 0),
        record_count: (historicalData?.record_count || 0) + (aggregatedDailyData?.record_count || 0)
      };
      // Only return if we have ANY data
      if (combined.record_count > 0 || historicalData || aggregatedDailyData) {
        return combined;
      }
      return null;
    }
    
    // For 2025-specific ranges, use historical data
    if (isHistorical && historicalData) return historicalData;
    
    // For 2026+ ranges, use localStorage aggregated data
    if (aggregatedDailyData && aggregatedDailyData.record_count > 0) return aggregatedDailyData;
    
    return null;
  }, [dateRange, isHistorical, historicalData, aggregatedDailyData]);

  // Projection data - Use real data for FIRA IND, mock for others
  const projectionData = useMemo(() => {
    // If FIRA IND with any data (historical or daily data from localStorage)
    if (hasRealData && activeData) {
      return [
        { key: 'subscribers', label: 'Subscribers', current: Math.round(activeData.total_subscribers || 0), target: 0, dailyAvg: 0, icon: Users, iconColor: '#3B82F6', remainingDays: 0, hasData: (activeData.total_subscribers || 0) > 0 },
        { key: 'leads', label: 'Leads', current: Math.round(activeData.total_leads || 0), target: 0, dailyAvg: 0, icon: Target, iconColor: '#E63946', remainingDays: 0, hasData: (activeData.total_leads || 0) > 0 },
        { key: 'closed', label: 'Closed', current: Math.round(activeData.total_closing || 0), target: 0, dailyAvg: 0, icon: Zap, iconColor: '#10B981', remainingDays: 0, hasData: (activeData.total_closing || 0) > 0 },
        { key: 'activeClient', label: 'Active Client', current: Math.round(activeData.total_active_client || 0), target: 0, dailyAvg: 0, icon: Users, iconColor: '#F59E0B', remainingDays: 0, hasData: (activeData.total_active_client || 0) > 0 },
        { key: 'tradedLot', label: 'Traded Lot', current: Math.round(activeData.total_traded_lot || 0), target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: '#F97316', remainingDays: 0, hasData: (activeData.total_traded_lot || 0) > 0 },
        // total_commission is NOW in MYR, no need to multiply
        { key: 'commissions', label: 'Commissions (RM)', current: Math.round(activeData.total_commission || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', color: '#8B5CF6', remainingDays: 0, hasData: (activeData.total_commission || 0) > 0, rateInfo: { usd: activeData.total_commission_usd || 0, rate: getRateForDate(dateFrom || dateTo || ''), period: toYearMonth(dateFrom || dateTo) } },
        { key: 'comPerLot', label: 'Com/Lot', current: (activeData.total_commission_usd || 0) > 0 && (activeData.total_traded_lot || 0) > 0 ? Math.round((activeData.total_commission_usd / activeData.total_traded_lot) * 100) / 100 : 0, target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: ((activeData.total_commission_usd || 0) / Math.max(activeData.total_traded_lot || 1, 1)) >= 12 ? '#10B981' : ((activeData.total_commission_usd || 0) / Math.max(activeData.total_traded_lot || 1, 1)) >= 8 ? '#F59E0B' : '#EF4444', prefix: '$', remainingDays: 0, hasData: (activeData.total_traded_lot || 0) > 0 },
      ];
    }
    // If FIRA IND but no data - show empty
    if (hasRealData) {
      return [
        { key: 'subscribers', label: 'Subscribers', current: 0, target: 0, dailyAvg: 0, icon: Users, iconColor: '#3B82F6', remainingDays: 0, hasData: false },
        { key: 'leads', label: 'Leads', current: 0, target: 0, dailyAvg: 0, icon: Target, iconColor: '#E63946', remainingDays: 0, hasData: false },
        { key: 'closed', label: 'Closed', current: 0, target: 0, dailyAvg: 0, icon: Zap, iconColor: '#10B981', remainingDays: 0, hasData: false },
        { key: 'activeClient', label: 'Active Client', current: 0, target: 0, dailyAvg: 0, icon: Users, iconColor: '#F59E0B', remainingDays: 0, hasData: false },
        { key: 'tradedLot', label: 'Traded Lot', current: 0, target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: '#F97316', remainingDays: 0, hasData: false },
        { key: 'commissions', label: 'Commissions', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', remainingDays: 0, hasData: false },
      ];
    }
    // Mock data for other IBs
    return [
      { key: 'subscribers', label: 'Subscribers', current: 890, target: 2500, dailyAvg: 30, icon: Users, iconColor: '#3B82F6', remainingDays: 25, hasData: true },
      { key: 'leads', label: 'Leads', current: 385, target: 800, dailyAvg: 15, icon: Target, iconColor: '#E63946', remainingDays: 25, hasData: true },
      { key: 'closed', label: 'Closed', current: 120, target: 240, dailyAvg: 5, icon: Zap, iconColor: '#10B981', remainingDays: 25, hasData: true },
      { key: 'activeClient', label: 'Active Client', current: 45, target: 50, dailyAvg: 0.5, icon: Users, iconColor: '#F59E0B', remainingDays: 25, hasData: true },
      { key: 'tradedLot', label: 'Traded Lot', current: 65.5, target: 100, dailyAvg: 1.8, icon: TrendingUp, iconColor: '#F97316', remainingDays: 25, hasData: true },
      { key: 'commissions', label: 'Commissions', current: 6200, target: 15000, dailyAvg: 350, icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', remainingDays: 25, hasData: true },
    ];
  }, [hasRealData, activeData]);

  // Performance Tracker Data - Use real data when available (from localStorage or API)
  // Now includes KPI targets from Channel Settings
  const performanceTrackerData = useMemo(() => {
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    
    // Helper to get target value from kpiTargets
    const getTarget = (dept, key) => {
      return kpiTargets[dept]?.[key] || 0;
    };
    
    // Helper to calculate daily target
    const getDailyTarget = (target) => {
      return target > 0 ? Math.round(target / daysInMonth) : 0;
    };
    
    // If FIRA IND with active data (either from localStorage or API)
    if (hasRealData && activeData) {
      return [
        { key: 'totalSpend', label: 'Total Spend', current: Math.round(activeData.total_ad_spent || 0), target: getTarget('marketing', 'totalSpend'), dailyTarget: getDailyTarget(getTarget('marketing', 'totalSpend')), icon: DollarSign, prefix: 'RM ', color: '#E63946', hasData: (activeData.total_ad_spent || 0) > 0 },
        { key: 'impressions', label: 'Impressions', current: Math.round(activeData.total_impressions || 0), target: getTarget('marketing', 'impressions'), dailyTarget: getDailyTarget(getTarget('marketing', 'impressions')), icon: Eye, color: '#3B82F6', hasData: (activeData.total_impressions || 0) > 0 },
        { key: 'linkClicks', label: 'Link Clicks', current: Math.round(activeData.total_clicks || 0), target: getTarget('marketing', 'linkClicks'), dailyTarget: getDailyTarget(getTarget('marketing', 'linkClicks')), icon: MousePointer, color: '#F97316', hasData: (activeData.total_clicks || 0) > 0 },
        { key: 'lpv', label: 'LPV', current: Math.round(activeData.total_lpv || 0), target: getTarget('marketing', 'lpv'), dailyTarget: getDailyTarget(getTarget('marketing', 'lpv')), icon: Eye, color: '#10B981', hasData: (activeData.total_lpv || 0) > 0 },
        { key: 'ctaButton', label: 'CTA Button', current: Math.round(activeData.total_cta || 0), target: getTarget('marketing', 'ctaButton'), dailyTarget: getDailyTarget(getTarget('marketing', 'ctaButton')), icon: MousePointer, color: '#F59E0B', hasData: (activeData.total_cta || 0) > 0 },
        { key: 'subscribers', label: 'Subscribers', current: Math.round(activeData.total_subscribers || 0), target: getTarget('sales', 'greeting'), dailyTarget: getDailyTarget(getTarget('sales', 'greeting')), icon: Users, color: '#3B82F6', hasData: (activeData.total_subscribers || 0) > 0 },
        { key: 'lead', label: 'Lead', current: Math.round(activeData.total_leads || 0), target: getTarget('sales', 'registration'), dailyTarget: getDailyTarget(getTarget('sales', 'registration')), icon: Target, color: '#E63946', hasData: (activeData.total_leads || 0) > 0 },
        { key: 'closed', label: 'Closed', current: Math.round(activeData.total_closing || 0), target: getTarget('sales', 'broker'), dailyTarget: getDailyTarget(getTarget('sales', 'broker')), icon: Zap, color: '#10B981', hasData: (activeData.total_closing || 0) > 0 },
        { key: 'deposit', label: 'Deposit', current: Math.round(activeData.total_deposit || 0), target: getTarget('sales', 'priceOffer'), dailyTarget: getDailyTarget(getTarget('sales', 'priceOffer')), icon: DollarSign, prefix: '$ ', color: '#F59E0B', hasData: (activeData.total_deposit || 0) > 0 },
        { key: 'activeClient', label: 'Active Client', current: Math.round(activeData.total_active_client || 0), target: getTarget('operations', 'retention'), dailyTarget: getDailyTarget(getTarget('operations', 'retention')), icon: Users, color: '#8B5CF6', hasData: (activeData.total_active_client || 0) > 0 },
        { key: 'tradedLot', label: 'Traded Lot', current: Math.round(activeData.total_traded_lot || 0), target: getTarget('operations', 'liveSessions'), dailyTarget: getDailyTarget(getTarget('operations', 'liveSessions')), icon: TrendingUp, color: '#F97316', hasData: (activeData.total_traded_lot || 0) > 0 },
        // total_commission is NOW in MYR, no need to multiply
        { key: 'commission', label: 'Commission (RM)', current: Math.round(activeData.total_commission || 0), target: (getTarget('marketing', 'cpl') || 0) * 1000, dailyTarget: getDailyTarget((getTarget('marketing', 'cpl') || 0) * 1000), icon: DollarSign, prefix: 'RM ', color: '#10B981', hasData: (activeData.total_commission || 0) > 0, rateInfo: { usd: activeData.total_commission_usd || 0, rate: getRateForDate(dateFrom || dateTo || ''), period: toYearMonth(dateFrom || dateTo) } },
      ];
    }
    // If FIRA IND but no data - show empty/no data with targets
    if (hasRealData) {
      return [
        { key: 'totalSpend', label: 'Total Spend', current: 0, target: getTarget('marketing', 'totalSpend'), dailyTarget: getDailyTarget(getTarget('marketing', 'totalSpend')), icon: DollarSign, prefix: 'RM ', color: '#E63946', hasData: false },
        { key: 'impressions', label: 'Impressions', current: 0, target: getTarget('marketing', 'impressions'), dailyTarget: getDailyTarget(getTarget('marketing', 'impressions')), icon: Eye, color: '#3B82F6', hasData: false },
        { key: 'linkClicks', label: 'Link Clicks', current: 0, target: getTarget('marketing', 'linkClicks'), dailyTarget: getDailyTarget(getTarget('marketing', 'linkClicks')), icon: MousePointer, color: '#F97316', hasData: false },
        { key: 'lpv', label: 'LPV', current: 0, target: getTarget('marketing', 'lpv'), dailyTarget: getDailyTarget(getTarget('marketing', 'lpv')), icon: Eye, color: '#10B981', hasData: false },
        { key: 'ctaButton', label: 'CTA Button', current: 0, target: getTarget('marketing', 'ctaButton'), dailyTarget: getDailyTarget(getTarget('marketing', 'ctaButton')), icon: MousePointer, color: '#F59E0B', hasData: false },
        { key: 'subscribers', label: 'Subscribers', current: 0, target: getTarget('sales', 'greeting'), dailyTarget: getDailyTarget(getTarget('sales', 'greeting')), icon: Users, color: '#3B82F6', hasData: false },
        { key: 'lead', label: 'Lead', current: 0, target: getTarget('sales', 'registration'), dailyTarget: getDailyTarget(getTarget('sales', 'registration')), icon: Target, color: '#E63946', hasData: false },
        { key: 'closed', label: 'Closed', current: 0, target: getTarget('sales', 'broker'), dailyTarget: getDailyTarget(getTarget('sales', 'broker')), icon: Zap, color: '#10B981', hasData: false },
        { key: 'deposit', label: 'Deposit', current: 0, target: getTarget('sales', 'priceOffer'), dailyTarget: getDailyTarget(getTarget('sales', 'priceOffer')), icon: DollarSign, prefix: '$ ', color: '#F59E0B', hasData: false },
        { key: 'activeClient', label: 'Active Client', current: 0, target: getTarget('operations', 'retention'), dailyTarget: getDailyTarget(getTarget('operations', 'retention')), icon: Users, color: '#8B5CF6', hasData: false },
        { key: 'tradedLot', label: 'Traded Lot', current: 0, target: getTarget('operations', 'liveSessions'), dailyTarget: getDailyTarget(getTarget('operations', 'liveSessions')), icon: TrendingUp, color: '#F97316', hasData: false },
        { key: 'commission', label: 'Commission', current: 0, target: (getTarget('marketing', 'cpl') || 0) * 1000, dailyTarget: getDailyTarget((getTarget('marketing', 'cpl') || 0) * 1000), icon: DollarSign, prefix: 'RM ', color: '#10B981', hasData: false },
      ];
    }
    // Mock data for other IBs
    return [
      { key: 'totalSpend', label: 'Total Spend', current: 28500, target: 45000, dailyTarget: 1500, icon: DollarSign, prefix: 'RM ', color: '#E63946', hasData: true },
      { key: 'impressions', label: 'Impressions', current: 1250000, target: 2000000, dailyTarget: 66667, icon: Eye, color: '#3B82F6', hasData: true },
      { key: 'linkClicks', label: 'Link Clicks', current: 3500, target: 20000, dailyTarget: 667, icon: MousePointer, color: '#F97316', hasData: true },
      { key: 'lpv', label: 'LPV', current: 5600, target: 9000, dailyTarget: 300, icon: Eye, color: '#10B981', hasData: true },
      { key: 'ctaButton', label: 'CTA Button', current: 2280, target: 3600, dailyTarget: 120, icon: MousePointer, color: '#F59E0B', hasData: true },
      { key: 'subscribers', label: 'Subscribers', current: 350, target: 2500, dailyTarget: 83, icon: Users, color: '#3B82F6', hasData: true },
      { key: 'lead', label: 'Lead', current: 385, target: 800, dailyTarget: 27, icon: Target, color: '#E63946', hasData: true },
      { key: 'closed', label: 'Closed', current: 120, target: 240, dailyTarget: 8, icon: Zap, color: '#10B981', hasData: true },
      { key: 'deposit', label: 'Deposit $', current: 11303, target: 25000, dailyTarget: 833, icon: DollarSign, prefix: '$', color: '#F59E0B', hasData: true },
      { key: 'activeClient', label: 'Active Client', current: 48, target: 50, dailyTarget: 2, icon: Users, color: '#8B5CF6', hasData: true },
      { key: 'tradedLot', label: 'Traded Lot', current: 105, target: 100, dailyTarget: 3.3, icon: TrendingUp, color: '#F97316', hasData: true },
      { key: 'commission', label: 'Commission', current: 6200, target: 15000, dailyTarget: 500, icon: DollarSign, prefix: 'RM ', color: '#10B981', hasData: true },
    ];
  }, [hasRealData, activeData, kpiTargets, currentDate, usdToMyr]);

  // Prepare realDailyData for shared PerformanceTracker component
  // This converts the marketing/sales data into a format the component expects
  const realDailyDataForTracker = useMemo(() => {
    if (!hasRealData) return null; // Non-FIRA IBs use mock data
    
    const marketingData = dailyData.marketing || [];
    const salesData = dailyData.sales || [];
    
    // Create a map of all dates with combined data
    const dateMap = new Map();
    
    // Process marketing data
    marketingData.forEach(day => {
      const totalSpend = safeNum(day.spend);
      const subscribers = (day.subsTG || 0) + (day.subsBot || 0);
      
      dateMap.set(day.date, {
        date: day.date,
        totalSpend,
        impressions: day.impressions || 0,
        linkClicks: day.clicks || 0,
        lpv: day.lpv || 0,
        ctaButton: day.cta || 0,
        subscribers,
        lead: day.leads || 0,
        closed: day.closed || 0,
        deposit: 0,
        activeClient: 0,
        tradedLot: 0,
        commission: 0
      });
    });
    
    // Merge sales data
    salesData.forEach(day => {
      const existing = dateMap.get(day.date) || {
        date: day.date,
        totalSpend: 0, impressions: 0, linkClicks: 0, lpv: 0, ctaButton: 0,
        subscribers: 0, lead: 0, closed: 0
      };
      
      existing.deposit = (function() {
        if (day.brokers) return Object.values(day.brokers).reduce((s,b) => s + ((b?.deposit_usd ?? b?.deposit) || 0), 0);
        return (day.brokerA?.deposit || 0) + (day.brokerB?.deposit || 0) + (day.brokerC?.deposit || 0);
      })();
      existing.activeClient = (function() {
        if (day.brokers) return Object.values(day.brokers).reduce((s,b) => s + (b?.active || 0), 0);
        return (day.brokerA?.active || 0) + (day.brokerB?.active || 0) + (day.brokerC?.active || 0);
      })();
      existing.tradedLot = (function() {
        if (day.brokers) return Object.values(day.brokers).reduce((s,b) => s + (b?.lots || 0), 0);
        return (day.brokerA?.lots || 0) + (day.brokerB?.lots || 0) + (day.brokerC?.lots || 0);
      })();
      existing.commission = (function() {
        if (day.brokers) return Object.values(day.brokers).reduce((s,b) => s + ((b?.commission_usd ?? b?.commission) || 0), 0);
        return (day.brokerA?.commission || 0) + (day.brokerB?.commission || 0) + (day.brokerC?.commission || 0);
      })();
      existing.closed = (existing.closed || 0) + (day.closed || 0);
      
      dateMap.set(day.date, existing);
    });
    
    // Convert map to array and sort by date
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [hasRealData, dailyData]);

  // Date calculations for tracker (still needed for subtitle)
  const currentDay = currentDate.getDate();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - currentDay;

  // Get performance tier: good (>=100%), average (80-99%), poor (<80%)
  const getPerformanceTier = (percent) => {
    const p = parseFloat(percent);
    if (p >= 100) return { tier: 'good', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.25)' };
    if (p >= 80) return { tier: 'average', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.25)' };
    return { tier: 'poor', color: '#E63946', bgColor: 'rgba(230, 57, 70, 0.06)', borderColor: 'rgba(230, 57, 70, 0.25)' };
  };

  const getProjection = (item) => {
    const adjustmentFactor = 1 + (adjustment / 100);
    const projected = item.current + (item.dailyAvg * item.remainingDays * adjustmentFactor);
    const currentPercent = ((item.current / item.target) * 100).toFixed(1);
    const projectedPercent = ((projected / item.target) * 100).toFixed(1);
    const status = projected >= item.target ? 'success' : projected >= item.target * 0.8 ? 'warning' : 'danger';
    return { projected, currentPercent, projectedPercent, status };
  };

  // Performance data filtered by date - uses activeData (localStorage OR API)
  const getFilteredPerformanceData = () => {
    // If FIRA IND with active data (from localStorage aggregation or API)
    if (hasRealData && activeData) {
      const spend = Math.round(activeData.total_ad_spent || 0);
      // total_commission is NOW in MYR (converted during aggregation), no need to multiply
      const commissionMyr = Math.round(activeData.total_commission || 0);
      const roi = spend > 0 ? (commissionMyr / spend) : 0;
      
      const getRoiTier = (r) => {
        if (r < 1) return { tier: 'BAD', color: '#E63946', bg: 'rgba(230, 57, 70, 0.15)' };
        if (r < 2) return { tier: 'AVERAGE', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' };
        if (r < 3) return { tier: 'GOOD', color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' };
        return { tier: 'EXCELLENT', color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.15)' };
      };
      
      const pm = prevMonthOverview || {};
      return [
        { label: 'Total Spend', value: spend > 0 ? `RM${spend.toLocaleString()}` : '-', trend: calcTrend(spend, pm.total_ad_spent), icon: DollarSign, color: '#E63946', hasData: spend > 0 },
        { label: 'Impressions', value: (activeData.total_impressions || 0) > 0 ? Math.round(activeData.total_impressions).toLocaleString() : '-', trend: 0, icon: Eye, color: '#3B82F6', hasData: (activeData.total_impressions || 0) > 0 },
        { label: 'Link Clicks', value: (activeData.total_clicks || 0) > 0 ? Math.round(activeData.total_clicks).toLocaleString() : '-', trend: 0, icon: MousePointer, color: '#F97316', hasData: (activeData.total_clicks || 0) > 0 },
        { label: 'LPV', value: (activeData.total_lpv || 0) > 0 ? Math.round(activeData.total_lpv).toLocaleString() : '-', trend: 0, icon: Eye, color: '#10B981', hasData: (activeData.total_lpv || 0) > 0 },
        { label: 'CTA Clicks', value: (activeData.total_cta || 0) > 0 ? Math.round(activeData.total_cta).toLocaleString() : '-', trend: 0, icon: MousePointer, color: '#F59E0B', hasData: (activeData.total_cta || 0) > 0 },
        { label: 'Subs FT', value: (activeData.total_subs_free_trial || 0) > 0 ? Math.round(activeData.total_subs_free_trial).toLocaleString() : '-', trend: 0, icon: Users, color: '#EC4899', hasData: (activeData.total_subs_free_trial || 0) > 0 },
        { label: 'Subscribers', value: (activeData.total_subscribers || 0) > 0 ? Math.round(activeData.total_subscribers).toLocaleString() : '-', trend: calcTrend(activeData.total_subscribers||0, pm.total_subscribers), icon: Users, color: '#3B82F6', hasData: (activeData.total_subscribers || 0) > 0 },
        { label: 'Leads', value: (activeData.total_leads || 0) > 0 ? Math.round(activeData.total_leads).toLocaleString() : '-', trend: calcTrend(activeData.total_leads||0, pm.total_leads), icon: Target, color: '#E63946', hasData: (activeData.total_leads || 0) > 0 },
        { label: 'Closed', value: (activeData.total_closing || 0) > 0 ? Math.round(activeData.total_closing).toLocaleString() : '-', trend: calcTrend(activeData.total_closing||0, pm.total_closing), icon: Zap, color: '#10B981', hasData: (activeData.total_closing || 0) > 0 },
        { label: 'Deposit', value: (activeData.total_deposit || 0) > 0 ? `$${Math.round(activeData.total_deposit).toLocaleString()}` : '-', trend: calcTrend(activeData.total_deposit||0, pm.total_deposit), icon: DollarSign, color: '#F59E0B', hasData: (activeData.total_deposit || 0) > 0 },
        { label: 'Signal', value: '-', trend: 0, icon: Send, color: '#06B6D4', hasData: false },
        { label: 'IB Activity', value: '-', trend: 0, icon: Activity, color: '#8B5CF6', hasData: false },
        { label: 'Active Client', value: (activeData.total_active_client || 0) > 0 ? Math.round(activeData.total_active_client).toLocaleString() : '-', trend: calcTrend(activeData.total_active_client||0, pm.total_commission), icon: Users, color: '#F97316', hasData: (activeData.total_active_client || 0) > 0 },
        { label: 'Traded Lot', value: (activeData.total_traded_lot || 0) > 0 ? Math.round(activeData.total_traded_lot).toLocaleString() : '-', trend: calcTrend(activeData.total_traded_lot||0, pm.total_commission), icon: TrendingUp, color: '#10B981', hasData: (activeData.total_traded_lot || 0) > 0 },
        { label: 'Commissions', value: commissionMyr > 0 ? `RM${commissionMyr.toLocaleString()}` : '-', trend: calcTrend(commissionMyr, pm.total_commission || 0), icon: DollarSign, color: '#E63946', hasData: commissionMyr > 0, rateInfo: { usd: activeData.total_commission_usd || 0, rate: getRateForDate(dateFrom || dateTo || ''), period: toYearMonth(dateFrom || dateTo) } },
        { label: 'ROI', value: roi > 0 ? roi.toFixed(2) : '-', trend: 0, icon: Award, color: getRoiTier(roi).color, isRoi: true, roiTier: getRoiTier(roi), hasData: roi > 0 },
      ];
    }
    
    // If FIRA IND but no data - show empty
    if (hasRealData) {
      const getRoiTier = (r) => ({ tier: 'N/A', color: '#6B7280', bg: 'rgba(107, 114, 128, 0.15)' });
      return [
        { label: 'Total Spend', value: '-', trend: 0, icon: DollarSign, color: '#E63946', hasData: false },
        { label: 'Impressions', value: '-', trend: 0, icon: Eye, color: '#3B82F6', hasData: false },
        { label: 'Link Clicks', value: '-', trend: 0, icon: MousePointer, color: '#F97316', hasData: false },
        { label: 'LPV', value: '-', trend: 0, icon: Eye, color: '#10B981', hasData: false },
        { label: 'CTA Clicks', value: '-', trend: 0, icon: MousePointer, color: '#F59E0B', hasData: false },
        { label: 'Subscribers', value: '-', trend: 0, icon: Users, color: '#3B82F6', hasData: false },
        { label: 'Leads', value: '-', trend: 0, icon: Target, color: '#E63946', hasData: false },
        { label: 'Closed', value: '-', trend: 0, icon: Zap, color: '#10B981', hasData: false },
        { label: 'Deposit', value: '-', trend: 0, icon: DollarSign, color: '#F59E0B', hasData: false },
        { label: 'Signal', value: '-', trend: 0, icon: Send, color: '#06B6D4', hasData: false },
        { label: 'IB Activity', value: '-', trend: 0, icon: Activity, color: '#8B5CF6', hasData: false },
        { label: 'Active Client', value: '-', trend: 0, icon: Users, color: '#F97316', hasData: false },
        { label: 'Traded Lot', value: '-', trend: 0, icon: TrendingUp, color: '#10B981', hasData: false },
        { label: 'Commissions', value: '-', trend: 0, icon: DollarSign, color: '#E63946', hasData: false },
        { label: 'ROI', value: '0', trend: 0, icon: Award, color: '#6B7280', isRoi: true, roiTier: getRoiTier(0), hasData: false },
      ];
    }
    
    // Mock data for other IBs
    const baseData = {
      'today': { multiplier: 0.05, trend: 5 },
      'yesterday': { multiplier: 0.04, trend: 3 },
      'this-week': { multiplier: 0.3, trend: 12 },
      'last-week': { multiplier: 0.25, trend: 10 },
      'this-month': { multiplier: 1, trend: 15 },
      'mtd': { multiplier: 1, trend: 15 },
      'last-month': { multiplier: 0.9, trend: 8 },
      'full': { multiplier: 3, trend: 20 },
      'custom': { multiplier: 0.5, trend: 7 }
    };
    const filter = baseData[dateRange] || baseData['mtd'];
    
    const spend = Math.round(43218 * filter.multiplier);
    const commission = Math.round(6200 * filter.multiplier);
    const roi = spend > 0 ? ((commission - spend) / spend) : 0;
    
    const getRoiTier = (r) => {
      if (r < 1) return { tier: 'BAD', color: '#E63946', bg: 'rgba(230, 57, 70, 0.15)' };
      if (r < 2) return { tier: 'AVERAGE', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' };
      if (r < 3) return { tier: 'GOOD', color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' };
      return { tier: 'EXCELLENT', color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.15)' };
    };
    
    const signals = Math.round(25 * filter.multiplier);
    const ibActivity = Math.round(12 * filter.multiplier);
    
    return [
      { label: 'Total Spend', value: `RM${spend.toLocaleString()}`, trend: filter.trend + 2.5, icon: DollarSign, color: '#E63946', hasData: true },
      { label: 'Impressions', value: Math.round(1994700 * filter.multiplier).toLocaleString(), trend: filter.trend - 3.7, icon: Eye, color: '#3B82F6', hasData: true },
      { label: 'Link Clicks', value: Math.round(19947 * filter.multiplier).toLocaleString(), trend: filter.trend - 1.9, icon: MousePointer, color: '#F97316', hasData: true },
      { label: 'LPV', value: Math.round(8967 * filter.multiplier).toLocaleString(), trend: filter.trend - 4.2, icon: Eye, color: '#10B981', hasData: true },
      { label: 'CTA Clicks', value: Math.round(3648 * filter.multiplier).toLocaleString(), trend: filter.trend - 3.8, icon: MousePointer, color: '#F59E0B', hasData: true },
      { label: 'Subscribers', value: Math.round(1483 * filter.multiplier).toLocaleString(), trend: filter.trend + 0.3, icon: Users, color: '#3B82F6', hasData: true },
      { label: 'Leads', value: Math.round(385 * filter.multiplier).toLocaleString(), trend: filter.trend + 3.5, icon: Target, color: '#E63946', hasData: true },
      { label: 'Closed', value: Math.round(120 * filter.multiplier).toLocaleString(), trend: filter.trend + 7.4, icon: Zap, color: '#10B981', hasData: true },
      { label: 'Deposit', value: `$${Math.round(11303 * filter.multiplier).toLocaleString()}`, trend: filter.trend + 4.8, icon: DollarSign, color: '#F59E0B', hasData: true },
      { label: 'Signal', value: signals.toString(), trend: filter.trend + 2.1, icon: Send, color: '#06B6D4', hasData: true },
      { label: 'IB Activity', value: ibActivity.toString(), trend: filter.trend + 8.5, icon: Activity, color: '#8B5CF6', hasData: true },
      { label: 'Active Client', value: Math.round(45 * filter.multiplier), trend: filter.trend - 9.8, icon: Users, color: '#F97316', hasData: true },
      { label: 'Traded Lot', value: (98.2 * filter.multiplier).toFixed(1), trend: filter.trend - 2.2, icon: TrendingUp, color: '#10B981', hasData: true },
      { label: 'Commissions', value: `RM${commission.toLocaleString()}`, trend: filter.trend + 1.5, icon: DollarSign, color: '#E63946', hasData: true },
      { label: 'ROI', value: roi.toFixed(2), trend: filter.trend + 3.0, icon: Award, color: getRoiTier(roi).color, isRoi: true, roiTier: getRoiTier(roi), hasData: true },
    ];
  };
  
  // Previous month data for comparison (computed in parent, passed to child components)
  const prevMonthOverview = useMemo(() => {
    if (!dailyData.marketing?.length && !dailyData.sales?.length) return {};
    const now = new Date();
    const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const pmDay = Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth(), 0).getDate());
    const pmEnd = new Date(now.getFullYear(), now.getMonth() - 1, pmDay);
    const pM = (dailyData.marketing||[]).filter(d => { const dt=new Date(d.date); return dt>=pm&&dt<=pmEnd; });
    const pS = (dailyData.sales||[]).filter(d => { const dt=new Date(d.date); return dt>=pm&&dt<=pmEnd; });
    return {
      total_ad_spent: pM.reduce((s,d) => s+safeNum(d.spend), 0),
      total_subscribers: pM.reduce((s,d) => s+safeInt(d.subsTG)+safeInt(d.subsBot), 0),
      total_leads: pS.reduce((s,d) => s+sumLeads(d), 0),
      total_closing: pS.reduce((s,d) => s+sumClosed(d), 0),
      total_deposit: pS.reduce((s,d) => s+sumDepositUsd(d), 0),
      // Previous-month commission uses per-row admin rate (matches current month formula)
      total_commission: pS.reduce((s,d) => s + sumCommissionUsd(d) * getRateForDate(d.date), 0),
    };
  }, [dailyData, getRateForDate]);
  const calcTrend = (c, p) => { if(!p||p===0) return c>0?100:0; return ((c-p)/p*100); };

  // Trend chart data - DYNAMIC based on date range FROM DAILY DATA
  const getFilteredTrendData = () => {
    // If FIRA IND, use real data from localStorage
    if (hasRealData) {
      // Get daily data from localStorage and filter by date range
      const marketingData = dailyData.marketing || [];
      const salesData = dailyData.sales || [];
      
      // Combine and filter by date range
      const filteredMarketing = marketingData.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      const filteredSales = salesData.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      // If no data in range, return empty
      if (!filteredMarketing.length && !filteredSales.length) return [];
      
      // Helper function to sum broker values
      const sumBrokers = (d, field) => {
        if (d.brokers) {
          return Object.values(d.brokers).reduce((sum, b) => {
            let v = b?.[field];
            if ((v === undefined || v === null) && field === 'commission') v = b?.commission_usd;
            if ((v === undefined || v === null) && field === 'deposit') v = b?.deposit_usd;
            return sum + (v || 0);
          }, 0);
        }
        return (d.brokerA?.[field] || 0) + (d.brokerB?.[field] || 0) + (d.brokerC?.[field] || 0);
      };
      
      // Build trend data by date
      const dateMap = new Map();
      
      // Add marketing data
      filteredMarketing.forEach(d => {
        const dateStr = new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
        const existing = dateMap.get(dateStr) || { date: dateStr, spend: 0, subscribers: 0, leads: 0, closed: 0, commission: 0, activeClient: 0, lotTraded: 0, deposit: 0 };
        existing.spend += safeNum(d.spend);
        existing.subscribers += (d.subsTG || 0) + (d.subsBot || 0);
        existing.leads += (d.leads || 0);
        existing.closed += (d.closed || 0);
        dateMap.set(dateStr, existing);
      });
      
      // Add sales data
      filteredSales.forEach(d => {
        const dateStr = new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
        const existing = dateMap.get(dateStr) || { date: dateStr, spend: 0, subscribers: 0, leads: 0, closed: 0, commission: 0, activeClient: 0, lotTraded: 0, deposit: 0 };
        existing.closed += (d.closed || 0);
        existing.activeClient += sumBrokers(d, 'active');
        existing.lotTraded += sumBrokers(d, 'lots');
        existing.commission += d.total_commission_usd !== undefined ? d.total_commission_usd : sumBrokers(d, 'commission_usd') || sumBrokers(d, 'commission');
        existing.deposit += d.total_deposit_usd !== undefined ? d.total_deposit_usd : sumBrokers(d, 'deposit_usd') || sumBrokers(d, 'deposit');
        dateMap.set(dateStr, existing);
      });
      
      // Convert to array and sort by date
      const result = Array.from(dateMap.values()).sort((a, b) => {
        const [dayA, monthA] = a.date.split('/');
        const [dayB, monthB] = b.date.split('/');
        return (parseInt(monthA) * 100 + parseInt(dayA)) - (parseInt(monthB) * 100 + parseInt(dayB));
      });
      
      return result;
    }
    
    // Mock data for non-FIRA IND
    const allData = [
      { date: '01/01', spend: 1200, subscribers: 45, leads: 12, closed: 3, commission: 180, activeClient: 42, lotTraded: 3.2 },
      { date: '02/01', spend: 1350, subscribers: 52, leads: 15, closed: 4, commission: 220, activeClient: 43, lotTraded: 3.8 },
      { date: '03/01', spend: 1180, subscribers: 48, leads: 14, closed: 5, commission: 195, activeClient: 43, lotTraded: 3.5 },
      { date: '04/01', spend: 1420, subscribers: 58, leads: 18, closed: 6, commission: 280, activeClient: 44, lotTraded: 4.2 },
      { date: '05/01', spend: 1550, subscribers: 65, leads: 22, closed: 7, commission: 350, activeClient: 44, lotTraded: 4.8 },
      { date: '06/01', spend: 1380, subscribers: 60, leads: 20, closed: 6, commission: 290, activeClient: 45, lotTraded: 4.1 },
      { date: '07/01', spend: 1480, subscribers: 68, leads: 24, closed: 8, commission: 380, activeClient: 45, lotTraded: 5.2 },
    ];
    
    return allData;
  };
  
  const trendData = getFilteredTrendData();
  
  // Broker performance data for pie charts - DYNAMIC based on aggregatedDailyData
  const getBrokerPerformanceData = () => {
    // Broker colors mapping
    const brokerColors = {
      'HFM': '#E63946',
      'OCTA': '#00A650',
      'VANTAGE': '#0052FF',
      'EXNESS': '#F59E0B',
      'IC MARKETS': '#8B5CF6',
      'Pepperstone': '#9333EA',
      'Moneta': '#06B6D4',
      'Valetax': '#84CC16',
    };
    
    // If we have real data, use it from aggregatedDailyData (filtered by date range)
    if (hasRealData) {
      // Calculate broker-specific totals from filtered sales data (2026)
      const salesData = dailyData.sales || [];
      const filteredSales = salesData.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      // Dynamically aggregate broker data from the new structure
      // ONLY include brokers that are enabled in broker config
      const brokerTotals = {};
      const enabledBrokerNames = new Set();
      Object.values(brokerConfig).forEach(cfg => {
        if (cfg.enabled && cfg.name) enabledBrokerNames.add(cfg.name.toLowerCase());
      });
      
      // Add 2026 data
      filteredSales.forEach(d => {
        // New format: d.brokers.octa, d.brokers.hfm etc.
        if (d.brokers) {
          // Normalize broker keys: strip whitespace, lowercase, and collapse
          // duplicate keys (e.g. "octa" + "OCTA" + "Octa ") into a single bucket.
          // This fixes the chart showing the same broker twice when production data
          // contains case-different keys from legacy imports.
          const normalizedByKey = {};
          Object.entries(d.brokers).forEach(([brokerKey, brokerData]) => {
            if (!brokerData || typeof brokerData !== 'object') return;
            const normKey = String(brokerKey || '').trim().toLowerCase();
            if (!normKey) return;
            if (!normalizedByKey[normKey]) normalizedByKey[normKey] = { ...brokerData, name: brokerData.name || brokerKey };
            else {
              // Merge numeric fields, keep first non-empty display name
              ['deposit_usd', 'deposit', 'active', 'lots', 'commission_usd', 'commission', 'commission_myr', 'deposit_myr']
                .forEach(f => { normalizedByKey[normKey][f] = (normalizedByKey[normKey][f] || 0) + (brokerData[f] || 0); });
              if (!normalizedByKey[normKey].name && brokerData.name) normalizedByKey[normKey].name = brokerData.name;
            }
          });
          Object.entries(normalizedByKey).forEach(([brokerKey, brokerData]) => {
            const displayName = (brokerData.name || brokerKey.charAt(0).toUpperCase() + brokerKey.slice(1)).trim();
            // Skip brokers not in active config
            if (enabledBrokerNames.size > 0 && !enabledBrokerNames.has(displayName.toLowerCase()) && !enabledBrokerNames.has(brokerKey.toLowerCase())) return;
            // Dedupe at bucket level too (case-insensitive name) — handles edge case
            // where two normalized keys still map to the same display name.
            const bucketKey = displayName.toLowerCase();
            if (!brokerTotals[bucketKey]) {
              brokerTotals[bucketKey] = { _displayName: displayName, deposit: 0, active: 0, lots: 0, commission_myr: 0, commission_usd: 0 };
            }
            brokerTotals[bucketKey].deposit += (brokerData.deposit_usd || brokerData.deposit || 0);
            brokerTotals[bucketKey].active += (brokerData.active || 0);
            brokerTotals[bucketKey].lots += (brokerData.lots || 0);
            brokerTotals[bucketKey].commission_usd += (brokerData.commission_usd || brokerData.commission || 0);
            // 2026 data: Use commission_myr if available, else convert from USD
            const commMyr = brokerData.commission_myr || (brokerData.commission_usd || brokerData.commission || 0) * usdToMyr;
            brokerTotals[bucketKey].commission_myr = (brokerTotals[bucketKey].commission_myr || 0) + commMyr;
          });
        } else {
          // Old format fallback: use broker config names
          const enabledEntries = Object.entries(brokerConfig).filter(([_, cfg]) => cfg.enabled && cfg.name);
          const keyMap = { brokerA: 0, brokerB: 1, brokerC: 2, brokerD: 3, brokerE: 4 };
          enabledEntries.forEach(([key, cfg]) => {
            const name = cfg.name;
            const bucketKey = String(name || key).trim().toLowerCase();
            if (!brokerTotals[bucketKey]) brokerTotals[bucketKey] = { _displayName: name, deposit: 0, active: 0, lots: 0, commission_myr: 0, commission_usd: 0 };
            brokerTotals[bucketKey].deposit += (d[key]?.deposit || 0);
            brokerTotals[bucketKey].active += (d[key]?.active || 0);
            brokerTotals[bucketKey].lots += (d[key]?.lots || 0);
            brokerTotals[bucketKey].commission_usd += (d[key]?.commission || 0);
            brokerTotals[bucketKey].commission_myr += (d[key]?.commission || 0) * usdToMyr;
          });
        }
      });
      
      // Add 2025 historical data if available (for Full Maximum or 2025 ranges)
      // Note: Historical 2025 commission from API is in USD (broker commission field is USD)
      if (historicalData && (dateRange === 'full' || dateRange?.startsWith('2025'))) {
        const addHistoric = (displayName, depositVal, activeVal, lotsVal, commVal) => {
          const k = displayName.toLowerCase();
          if (!brokerTotals[k]) brokerTotals[k] = { _displayName: displayName, deposit: 0, active: 0, lots: 0, commission_myr: 0, commission_usd: 0 };
          brokerTotals[k].deposit += (depositVal || 0);
          brokerTotals[k].active = Math.max(brokerTotals[k].active, activeVal || 0);
          brokerTotals[k].lots += (lotsVal || 0);
          brokerTotals[k].commission_myr = (brokerTotals[k].commission_myr || 0) + (commVal || 0) * usdToMyr;
        };
        addHistoric('Octa', historicalData.octa_deposit, historicalData.octa_active, historicalData.octa_lot, historicalData.octa_commission);
        addHistoric('HFM', historicalData.hfm_deposit, historicalData.hfm_active, historicalData.hfm_lot, historicalData.hfm_commission);
        addHistoric('Vantage', historicalData.vantage_deposit, historicalData.vantage_active, historicalData.vantage_lot, historicalData.vantage_commission);
      }
      
      // Convert to array format for charts
      const brokerNames = Object.keys(brokerTotals);
      const displayLabel = (k) => (brokerTotals[k]._displayName || k).toUpperCase();
      
      return {
        deposit: brokerNames.map(name => ({
          name: displayLabel(name),
          value: Math.round(brokerTotals[name].deposit),
          color: brokerColors[displayLabel(name)] || brokerColors[name] || '#6B7280'
        })).filter(b => b.value > 0),
        activeClient: brokerNames.map(name => ({
          name: displayLabel(name),
          value: Math.round(brokerTotals[name].active),
          color: brokerColors[displayLabel(name)] || brokerColors[name] || '#6B7280'
        })).filter(b => b.value > 0),
        lotTraded: brokerNames.map(name => ({
          name: displayLabel(name),
          value: Math.round(brokerTotals[name].lots),
          color: brokerColors[displayLabel(name)] || brokerColors[name] || '#6B7280'
        })).filter(b => b.value > 0),
        commissions: brokerNames.map(name => ({
          name: displayLabel(name),
          // Commission is now in MYR directly
          value: Math.round(brokerTotals[name].commission_myr || 0),
          color: brokerColors[displayLabel(name)] || brokerColors[name] || '#6B7280'
        })).filter(b => b.value > 0),
        comPerLot: brokerNames.map(name => {
          const cpl = brokerTotals[name].lots > 0 ? brokerTotals[name].commission_usd / brokerTotals[name].lots : 0;
          return {
            name: displayLabel(name),
            value: Math.round(cpl * 100) / 100,
            color: cpl >= 12 ? '#10B981' : cpl >= 8 ? '#F59E0B' : '#EF4444'
          };
        }).filter(b => b.value > 0),
      };
    }
    
    // Mock data for other IBs
    const baseData = {
      'today': { multiplier: 0.05 },
      'yesterday': { multiplier: 0.04 },
      'this-week': { multiplier: 0.3 },
      'last-week': { multiplier: 0.25 },
      'this-month': { multiplier: 1 },
      'mtd': { multiplier: 1 },
      'last-month': { multiplier: 0.9 },
      'full': { multiplier: 3 },
      'custom': { multiplier: 0.5 }
    };
    const filter = baseData[dateRange] || baseData['mtd'];
    
    // Mock base values per broker
    const baseBrokerData = {
      'HFM': { deposit: 6500, activeClient: 28, lotTraded: 58.5, commissions: 3800 },
      'OCTA': { deposit: 4803, activeClient: 17, lotTraded: 39.7, commissions: 2400 },
      'VANTAGE': { deposit: 3200, activeClient: 12, lotTraded: 28.3, commissions: 1600 },
      'EXNESS': { deposit: 2100, activeClient: 8, lotTraded: 18.5, commissions: 950 },
      'IC MARKETS': { deposit: 1800, activeClient: 6, lotTraded: 14.2, commissions: 720 },
    };
    
    // Filter only active brokers
    const filteredBrokers = activeBrokers.filter(b => brokerColors[b]);
    
    return {
      deposit: filteredBrokers.map((broker) => ({
        name: broker,
        value: Math.round((baseBrokerData[broker]?.deposit || 1000) * filter.multiplier),
        color: brokerColors[broker] || '#6B7280'
      })),
      activeClient: filteredBrokers.map((broker) => ({
        name: broker,
        value: Math.round((baseBrokerData[broker]?.activeClient || 5) * filter.multiplier),
        color: brokerColors[broker] || '#6B7280'
      })),
      lotTraded: filteredBrokers.map((broker) => ({
        name: broker,
        value: Math.round((baseBrokerData[broker]?.lotTraded || 10) * filter.multiplier * 10) / 10,
        color: brokerColors[broker] || '#6B7280'
      })),
      commissions: filteredBrokers.map((broker) => ({
        name: broker,
        value: Math.round((baseBrokerData[broker]?.commissions || 500) * filter.multiplier),
        color: brokerColors[broker] || '#6B7280'
      })),
    };
  };
  
  const brokerData = getBrokerPerformanceData();

  return (
    <>
      {/* Sticky Header */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel Overview</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">Performance Metrics & Insights</p>
            <LastEditedBy name={editMeta.name} timestamp={editMeta.time} />
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button 
              variant="outline" 
              size="sm"
              className="border-[hsl(var(--border))] hover:border-[#F59E0B] hover:bg-[#F59E0B]/10 text-white text-xs"
              onClick={() => setShowSummaryModal(true)}
            >
              <FileText size={14} className="mr-1.5" />
              SUMMARY
            </Button>

            <Button variant="outline" size="icon" className="border-[hsl(var(--border))] hover:border-[#E63946] hover:bg-[#E63946]/10 text-white h-8 w-8">
              <Bell size={14} />
            </Button>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-auto sm:w-56 justify-start bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))] text-xs">
                  <CalendarIcon size={14} className="mr-1.5" />
                  {dateRange === 'today' && 'Today'}
                  {dateRange === 'yesterday' && 'Yesterday'}
                  {dateRange === 'this-week' && 'This Week'}
                  {dateRange === 'last-week' && 'Last Week'}
                  {dateRange === 'this-month' && 'This Month'}
                  {dateRange === 'mtd' && `MTD: ${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}`}
                  {dateRange === 'last-month' && 'Last Month'}
                  {dateRange === 'full' && 'Full Maximum'}
                  {dateRange === '2025-full' && '📊 Full Year 2025'}
                  {dateRange === '2025-q1' && '📊 Q1 2025'}
                  {dateRange === '2025-q2' && '📊 Q2 2025'}
                  {dateRange === '2025-q3' && '📊 Q3 2025'}
                  {dateRange === '2025-q4' && '📊 Q4 2025'}
                  {dateRange === 'custom' && (dateFrom && dateTo ? `${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}` : 'Custom Range')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" align="end">
                <div className="p-4 border-b border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-3">Quick Select</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['today', 'yesterday', 'this-week', 'last-week', 'this-month', 'last-month'].map((range) => (
                      <Button 
                        key={range} 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setDateRange(range)} 
                        className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === range ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}
                      >
                        {range.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Button>
                    ))}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setDateRange('full')} 
                      className={`text-white hover:bg-[hsl(var(--surface-elevated))] col-span-2 justify-start ${dateRange === 'full' ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}
                    >
                      Full Maximum
                    </Button>
                  </div>
                </div>
                {/* Historical 2025 Section */}
                <div className="p-4 border-t border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F59E0B] mb-3">📊 Historical 2025</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setDateRange('2025-full')} 
                      className={`text-white hover:bg-[hsl(var(--surface-elevated))] col-span-2 justify-start ${dateRange === '2025-full' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : ''}`}
                    >
                      Full Year 2025
                    </Button>
                    {['2025-q1', '2025-q2', '2025-q3', '2025-q4'].map((q) => (
                      <Button 
                        key={q} 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setDateRange(q)} 
                        className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === q ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : ''}`}
                      >
                        {q.replace('2025-', '').toUpperCase()} 2025
                      </Button>
                    ))}
                  </div>
                </div>
                {/* Custom Date Range Picker */}
                <div className="p-4 border-t border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-3">Custom Range</p>
                  <Popover 
                    open={showCustomDatePicker} 
                    onOpenChange={(open) => {
                      // Reset selection when opening picker
                      if (open) {
                        setCustomDateFrom(null);
                        setCustomDateTo(null);
                      }
                      setShowCustomDatePicker(open);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={`w-full justify-start border-[hsl(var(--border))] text-white ${dateRange === 'custom' ? 'bg-[#E63946]/20 border-[#E63946]' : ''}`}
                      >
                        <CalendarIcon size={14} className="mr-2" />
                        {dateRange === 'custom' && dateFrom && dateTo 
                          ? `${format(dateFrom, 'dd MMM yyyy')} - ${format(dateTo, 'dd MMM yyyy')}`
                          : 'Pick custom dates'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" align="center" side="left">
                      <Calendar
                        mode="range"
                        selected={{ from: customDateFrom, to: customDateTo }}
                        onSelect={(range) => {
                          if (range?.from && !range?.to) {
                            // First click - set start date
                            setCustomDateFrom(range.from);
                            setCustomDateTo(null);
                          } else if (range?.from && range?.to) {
                            // Second click - set end date and close automatically
                            setCustomDateFrom(range.from);
                            setCustomDateTo(range.to);
                            setDateRange('custom');
                            setShowCustomDatePicker(false);
                          }
                        }}
                        className="rounded-md"
                        numberOfMonths={1}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {/* Scrollable Content - NO horizontal scroll on main page */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-8 py-6 space-y-6 w-full" style={{ maxWidth: '100%' }}>
          
          {/* PERFORMANCE PROJECTIONS - Using Monthly Targets for target values */}
          <OverviewProjections 
            activeData={activeData}
            hasRealData={hasRealData}
            usdToMyr={usdToMyr}
            kpiTargets={kpiTargets}
            monthlyTargets={monthlyTargets}
            prevMonthData={prevMonthOverview}
            calcTrend={calcTrend}
          />

          {/* OVERALL PERFORMANCE */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={BarChart3} title="Overall Performance" subtitle="Key Metrics Scorecard" />
              
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {getFilteredPerformanceData().map((metric, idx) => {
                    const Icon = metric.icon;
                    const isPositive = metric.trend >= 0;
                    const hasData = metric.hasData !== false; // Default to true if not specified
                    
                    // Special handling for ROI metric
                    if (metric.isRoi) {
                      return (
                        <div key={idx} className="relative group">
                          <div className="absolute -inset-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 blur-lg" style={{ background: `radial-gradient(circle, ${metric.roiTier.color}30, transparent)` }} />
                          <div 
                            className={`relative rounded-lg p-4 transition-all hover:shadow-md border-2 ${!hasData ? 'opacity-60' : ''}`}
                            style={{ 
                              backgroundColor: metric.roiTier.bg,
                              borderColor: metric.roiTier.color
                            }}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{metric.label}</p>
                              <Icon size={18} style={{ color: metric.roiTier.color }} />
                            </div>
                            <p className={`text-2xl font-mono font-bold mb-1 ${hasData ? 'text-white' : 'text-[#6B7280]'}`}>
                              {metric.value}x
                            </p>
                            <Badge 
                              className="text-[9px] font-bold px-2"
                              style={{ backgroundColor: `${metric.roiTier.color}30`, color: metric.roiTier.color }}
                            >
                              {metric.roiTier.tier}
                            </Badge>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div key={idx} className="relative group">
                        <div className="absolute -inset-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 blur-lg" style={{ background: `radial-gradient(circle, ${metric.color}30, transparent)` }} />
                        <div 
                          className={`relative bg-[hsl(var(--surface-elevated))] border rounded-lg p-4 transition-all hover:shadow-md ${!hasData ? 'opacity-60' : ''}`}
                          style={{ borderColor: `${metric.color}25` }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-1">
                              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{metric.label}</p>
                              {metric.rateInfo ? (
                                <RateInfoBadge usd={metric.rateInfo.usd} rate={metric.rateInfo.rate} period={metric.rateInfo.period} />
                              ) : null}
                            </div>
                            <Icon size={18} style={{ color: hasData ? metric.color : '#6B7280' }} />
                          </div>
                          <p className={`text-2xl font-mono font-bold mb-2 ${hasData ? 'text-white' : 'text-[#6B7280]'}`}>
                            {metric.value}
                          </p>
                          {hasData && metric.trend !== 0 && (
                            <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              <span>{Math.abs(metric.trend).toFixed(1)}%</span>
                            </div>
                          )}
                          {!hasData && (
                            <div className="flex items-center gap-1 text-xs text-[#6B7280]">
                              <span>No data available</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          
          {/* PERFORMANCE BY BROKER - Pie Charts */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={PieChart} title="Performance by Broker" subtitle="Comparison across active brokers" />
              
              <div className="p-3 sm:p-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {[
                    { key: 'deposit', label: 'Deposit', prefix: '$', data: brokerData.deposit },
                    { key: 'activeClient', label: 'Active Client', data: brokerData.activeClient },
                    { key: 'lotTraded', label: 'Lot Traded', data: brokerData.lotTraded },
                    { key: 'commissions', label: 'Commissions', prefix: 'RM', data: brokerData.commissions },
                    { key: 'comPerLot', label: 'Com/Lot', prefix: '$', data: brokerData.comPerLot || [] },
                  ].map((category) => {
                    const total = category.data.reduce((sum, d) => sum + d.value, 0);
                    return (
                      <div key={category.key} className="bg-[hsl(var(--surface-elevated))] rounded-xl p-4 border border-[hsl(var(--border))]">
                        <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-4 text-center">{category.label}</p>
                        <div className="h-[150px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                              <Pie
                                data={category.data}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={55}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {category.data.map((entry, idx) => (
                                  <Cell key={`cell-${idx}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#FFFFFF', 
                                  border: '1px solid #E5E7EB', 
                                  borderRadius: '8px',
                                  color: '#1F2937',
                                  padding: '8px 12px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                                itemStyle={{ color: '#1F2937' }}
                                labelStyle={{ color: '#1F2937', fontWeight: 'bold' }}
                                formatter={(value, name, props) => {
                                  const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                  return [`${category.prefix || ''}${value.toLocaleString()} (${percent}%)`, props.payload.name];
                                }}
                              />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>
                        {/* Legend */}
                        <div className="mt-3 space-y-2">
                          {category.data.map((item) => {
                            const avg = weekdayCount > 0 ? item.value / weekdayCount : 0;
                            const showAvg = avg > 0 && category.key !== 'comPerLot'; // com/lot is already a ratio
                            const pct = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
                            return (
                              <div key={item.name} className="flex flex-col gap-0.5">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                    <span className="text-[11px] text-[#D1D5DB] truncate">{item.name}</span>
                                  </div>
                                  <span className="text-[10px] text-[#6B7280] font-mono tabular-nums">{pct}%</span>
                                </div>
                                <div className="flex items-baseline justify-between pl-4">
                                  <span className="text-[13px] text-white font-mono font-semibold tabular-nums">
                                    {category.prefix || ''}{item.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                  </span>
                                  {showAvg && (
                                    <span
                                      className="text-[10px] text-[#6B7280] font-mono tabular-nums"
                                      title={`Weekday average · ${weekdayCount} weekdays in range`}
                                    >
                                      avg {category.prefix || ''}{avg.toLocaleString(undefined, { maximumFractionDigits: avg < 10 ? 2 : 0 })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* TREND CHART - Renamed: Spend > Closed */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={PieChart} title="Trend Analysis : Spend > Closed" subtitle="Marketing funnel performance over time" />
              
              <div className="p-6">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                    <YAxis yAxisId="left" stroke="#E63946" />
                    <YAxis yAxisId="right" orientation="right" stroke="#3B82F6" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--surface-elevated))', 
                        border: '1px solid hsl(var(--border))', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#9CA3AF', paddingTop: '16px' }} />
                    <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#E63946" strokeWidth={3} name="Spend (RM)" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} name="Subscribers" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2} name="Leads" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2} name="Closed" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* BROKER METRICS TREND - Renamed: Active Client > Commissions */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#8B5CF6]/10 via-transparent to-[#3B82F6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={BarChart3} title="Trend Analysis : Active Client > Commissions" subtitle="Broker performance metrics over time" />
              
              <div className="p-6">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E63946" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#E63946" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorActiveClient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorLotTraded" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                    <YAxis yAxisId="left" stroke="#E63946" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--surface-elevated))', 
                        border: '1px solid hsl(var(--border))', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#9CA3AF', paddingTop: '16px' }} />
                    <Area yAxisId="left" type="monotone" dataKey="commission" stroke="#E63946" fill="url(#colorCommission)" strokeWidth={2} name="Commission (RM)" />
                    <Area yAxisId="right" type="monotone" dataKey="activeClient" stroke="#3B82F6" fill="url(#colorActiveClient)" strokeWidth={2} name="Active Clients" />
                    <Area yAxisId="right" type="monotone" dataKey="lotTraded" stroke="#10B981" fill="url(#colorLotTraded)" strokeWidth={2} name="Lot Traded" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* LEADS BREAKDOWN BY DEPARTMENT */}
          {/* TEAM COMPARISON - Conversion Rates (Vertical Funnel Layout) */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={BarChart3} title="Team Comparison: Conversion Rates" subtitle="Funnel flow - top to bottom (spot bottlenecks easily)" action={
                <KpiHelpButton title="Team Comparison: Conversion Rates" groups={[
                  { label: 'Marketing (M)', customKpis: CONVERSION_RATE_KPIS },
                  { label: 'Sales (S)',     customKpis: PIPELINE_STUCK_KPIS },
                  { label: 'Operations (IB)', customKpis: OPS_CONVERSION_KPIS },
                ]} />
              } />
              
              <div className="p-3 sm:p-6">
                {(() => {
                  const getColor = (rate, thresholds) => {
                    if (rate >= thresholds.excellent) return '#10B981';
                    if (rate >= thresholds.good) return '#3B82F6';
                    if (rate >= thresholds.average) return '#F59E0B';
                    return '#E63946';
                  };
                  
                  // Marketing data (Impressions → Closing Rate) - from activeData
                  const mImpressions = activeData?.total_impressions || 0;
                  const mClicks = activeData?.total_clicks || 0;
                  const mLpv = activeData?.total_lpv || 0;
                  const mCta = activeData?.total_cta || 0;
                  const mSubs = activeData?.total_subscribers || 0;
                  const mLeads = activeData?.total_leads || 0;
                  const mClosed = activeData?.total_closing || 0;
                  
                  // Sales Pipeline data - aggregate from dailyData.sales (REAL DATA)
                  const salesPipelineData = (() => {
                    if (!dailyData?.sales?.length) return { leads: 0, greeting: 0, tick: 0, registration: 0, broker: 0, offer: 0, closed: 0 };
                    
                    // Filter by date range
                    const filteredSales = dailyData.sales.filter(d => {
                      const itemDate = new Date(d.date);
                      return itemDate >= dateFrom && itemDate <= dateTo;
                    });
                    
                    let totalLeads = 0, totalGreeting = 0, totalTick = 0, totalRegistration = 0, totalBroker = 0, totalOffer = 0, totalClosed = 0;
                    
                    filteredSales.forEach(d => {
                      // Aggregate leads from all sources (M, S, IB)
                      const leadsM = d.leadsM || {};
                      const leadsS = d.leadsS || {};
                      const leadsIB = d.leadsIB || {};
                      totalLeads += (leadsM.new || 0) + (leadsM.prospect || 0) + (leadsM.vip || 0) +
                                    (leadsS.new || 0) + (leadsS.prospect || 0) + (leadsS.vip || 0) +
                                    (leadsIB.new || 0) + (leadsIB.prospect || 0) + (leadsIB.vip || 0);
                      // Pipeline stages
                      totalGreeting += (d.greeting || 0);
                      totalTick += (d.oneTick || 0);
                      totalRegistration += (d.registration || 0);
                      totalBroker += (d.broker || 0);
                      totalOffer += (d.priceOffer || 0);
                      totalClosed += (d.closed || 0);
                    });
                    
                    return { leads: totalLeads, greeting: totalGreeting, tick: totalTick, registration: totalRegistration, broker: totalBroker, offer: totalOffer, closed: totalClosed };
                  })();
                  
                  // Operations data (Reach → Closed) - from activeData/historicalData
                  const oSubs = activeData?.telegram_subscribers || 0;
                  const oReach = activeData?.avg_views_per_post || 0;
                  const oReactions = activeData?.avg_reactions_per_post || 0;
                  const oLeadsIB = activeData?.total_leads_ib || 0;
                  const oClosed = salesPipelineData.closed || mClosed;
                  
                  // Marketing Conversion Metrics - EXACT SAME AS Channel Performance
                  // Clicks Conv = Clicks / Impressions, LPV Conv = LPV / Clicks, etc.
                  const marketingMetrics = [
                    { name: 'Clicks Conv', rate: mImpressions > 0 ? (mClicks / mImpressions) * 100 : 0, desc: 'Impressions → Clicks', thresholds: { excellent: 10, good: 5, average: 2 } },
                    { name: 'LPV Conv', rate: mClicks > 0 ? (mLpv / mClicks) * 100 : 0, desc: 'Clicks → LPV', thresholds: { excellent: 80, good: 60, average: 40 } },
                    { name: 'CTA Conv', rate: mLpv > 0 ? (mCta / mLpv) * 100 : 0, desc: 'LPV → CTA', thresholds: { excellent: 30, good: 20, average: 10 } },
                    { name: 'Subs Conv', rate: mCta > 0 ? (mSubs / mCta) * 100 : 0, desc: 'CTA → Subs', thresholds: { excellent: 10, good: 5, average: 2 } },
                    { name: 'Leads Conv', rate: mSubs > 0 ? (mLeads / mSubs) * 100 : 0, desc: 'Subs → Leads', thresholds: { excellent: 80, good: 50, average: 30 } },
                    { name: 'Closing Rate', rate: mLeads > 0 ? (mClosed / mLeads) * 100 : 0, desc: 'Leads → Closed', thresholds: { excellent: 40, good: 25, average: 10 } },
                  ];
                  
                  // Sales Pipeline Stuck Rate - SAME AS Channel Performance > Sales > Pipeline Stuck Rate
                  // Lower stuck % is BETTER (except Closing Rate - higher is better)
                  const sLeads = salesPipelineData.leads || mLeads;
                  const salesMetrics = [
                    { name: 'Greeting Stuck', rate: sLeads > 0 ? (salesPipelineData.greeting / sLeads) * 100 : 0, isStuck: true, desc: '% stuck at Greeting', thresholds: { excellent: 5, good: 15, average: 30 } },
                    { name: '1 Tick Stuck', rate: sLeads > 0 ? (salesPipelineData.tick / sLeads) * 100 : 0, isStuck: true, desc: '% stuck at 1 Tick', thresholds: { excellent: 5, good: 15, average: 30 } },
                    { name: 'Reg Stuck', rate: sLeads > 0 ? (salesPipelineData.registration / sLeads) * 100 : 0, isStuck: true, desc: '% stuck at Registration', thresholds: { excellent: 5, good: 15, average: 30 } },
                    { name: 'Broker Stuck', rate: sLeads > 0 ? (salesPipelineData.broker / sLeads) * 100 : 0, isStuck: true, desc: '% stuck at Broker', thresholds: { excellent: 5, good: 15, average: 30 } },
                    { name: 'Offer Stuck', rate: sLeads > 0 ? (salesPipelineData.offer / sLeads) * 100 : 0, isStuck: true, desc: '% stuck at Offer', thresholds: { excellent: 5, good: 15, average: 30 } },
                    { name: 'Closing Rate', rate: sLeads > 0 ? (salesPipelineData.closed / sLeads) * 100 : 0, isStuck: false, desc: '% Closed', thresholds: { excellent: 40, good: 25, average: 10 } },
                  ];
                  
                  // Operations Conversion Rates - SAME AS Channel Performance > Operation > Conversion Rates Detail
                  const operationsMetrics = [
                    { name: 'Reach Conv', rate: oSubs > 0 ? (oReach / oSubs) * 100 : 0, desc: 'Subs → Reach', thresholds: { excellent: 10, good: 5, average: 2 } },
                    { name: 'Engage Conv', rate: oReach > 0 ? (oReactions / oReach) * 100 : 0, desc: 'Reach → Reactions', thresholds: { excellent: 5, good: 3, average: 1 } },
                    { name: 'Lead Conv', rate: oReactions > 0 ? (oLeadsIB / oReactions) * 100 : 0, desc: 'Reactions → Leads', thresholds: { excellent: 50, good: 30, average: 15 } },
                    { name: 'Closing Rate', rate: oLeadsIB > 0 ? (oClosed / oLeadsIB) * 100 : 0, desc: 'Leads → Closed', thresholds: { excellent: 40, good: 25, average: 10 } },
                  ];
                  
                  // Color function - for stuck rates, LOWER is better (reversed)
                  const getStuckColor = (rate, thresholds) => {
                    if (rate <= thresholds.excellent) return '#10B981'; // Excellent - very few stuck
                    if (rate <= thresholds.good) return '#3B82F6'; // Good
                    if (rate <= thresholds.average) return '#F59E0B'; // Average
                    return '#E63946'; // Poor - too many stuck
                  };
                  
                  // Render vertical funnel metric
                  const renderFunnelMetric = (metric, idx, total, deptColor, isSalesStuck = false) => {
                    const hasData = metric.rate > 0 || (metric.isStuck !== undefined);
                    // For sales stuck rate, use getStuckColor (lower is better, except Closing Rate)
                    let metricColor;
                    if (isSalesStuck && metric.isStuck !== undefined) {
                      if (metric.isStuck) {
                        metricColor = hasData ? getStuckColor(metric.rate, metric.thresholds) : '#6B7280';
                      } else {
                        metricColor = hasData ? getColor(metric.rate, metric.thresholds) : '#6B7280';
                      }
                    } else {
                      metricColor = hasData ? getColor(metric.rate, metric.thresholds) : '#6B7280';
                    }
                    const widthPercent = isSalesStuck ? 100 : (100 - (idx * (60 / total))); // No funnel for stuck rates
                    
                    return (
                      <div key={metric.name} className="relative">
                        {/* Connector line */}
                        {idx > 0 && !isSalesStuck && (
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0.5 h-2" style={{ backgroundColor: `${deptColor}40` }} />
                        )}
                        <div 
                          className="mx-auto p-3 rounded-lg border transition-all hover:shadow-lg"
                          style={{ 
                            width: `${widthPercent}%`, 
                            backgroundColor: `${metricColor}15`, 
                            borderColor: `${metricColor}40`,
                            borderLeftWidth: '3px',
                            borderLeftColor: metricColor,
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: metricColor }} />
                              <div>
                                <p className="text-xs font-medium text-white">{metric.name}</p>
                                {metric.desc && <p className="text-[9px] text-[#6B7280]">{metric.desc}</p>}
                              </div>
                            </div>
                            <p className="text-sm sm:text-lg font-mono font-bold truncate" style={{ color: metricColor }}>
                              {(hasData && metric.rate > 0) ? `${metric.rate.toFixed(1)}%` : '-'}
                            </p>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2 h-1 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(metric.rate, 100)}%`, backgroundColor: metricColor }} />
                          </div>
                        </div>
                        {/* Arrow down - only for funnel (not stuck rates) */}
                        {idx < total - 1 && !isSalesStuck && (
                          <div className="flex justify-center py-1">
                            <ChevronDown size={16} style={{ color: `${deptColor}60` }} />
                          </div>
                        )}
                      </div>
                    );
                  };
                  
                  return (
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Marketing Column */}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#E63946]">
                          <div className="w-3 h-3 rounded-full bg-[#E63946]" />
                          <p className="text-sm font-bold text-[#E63946]">Marketing (M)</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-3">Impressions → Clicks → LPV → CTA → Subs → Leads → Closed</p>
                        <div className="space-y-1">
                          {marketingMetrics.map((m, idx) => renderFunnelMetric(m, idx, marketingMetrics.length, '#E63946'))}
                        </div>
                      </div>
                      
                      {/* Vertical Separator */}
                      <div className="w-px bg-gradient-to-b from-transparent via-[hsl(var(--border))] to-transparent" />
                      
                      {/* Sales Column */}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#3B82F6]">
                          <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                          <p className="text-sm font-bold text-[#3B82F6]">Sales (S)</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-3">Pipeline Stuck Rate (Lower = Better)</p>
                        <div className="space-y-2">
                          {salesMetrics.map((m, idx) => renderFunnelMetric(m, idx, salesMetrics.length, '#3B82F6', true))}
                        </div>
                      </div>
                      
                      {/* Vertical Separator */}
                      <div className="w-px bg-gradient-to-b from-transparent via-[hsl(var(--border))] to-transparent" />
                      
                      {/* Operations Column */}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#10B981]">
                          <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                          <p className="text-sm font-bold text-[#10B981]">Operations (IB)</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-3">Subs → Reach → Leads → Closed</p>
                        <div className="space-y-1">
                          {operationsMetrics.map((m, idx) => renderFunnelMetric(m, idx, operationsMetrics.length, '#10B981'))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* TEAM COMPARISON - Cost/Performance Metrics (Different per department) */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={DollarSign} title="Team Comparison: Performance Metrics" subtitle="Department-specific cost & performance indicators" action={
                <KpiHelpButton title="Team Comparison: Performance Metrics" groups={[
                  { label: 'Marketing', metrics: ['CPM','CPLC','CPLPV','CPS','CPS_FT','CPL','CPC_CLIENT'] },
                  { label: 'Sales',     metrics: ['CPC_CLIENT_SALES','RENEWAL_RATE','AVG_DEPOSIT','CPL_SALES','CLOSING_RATE'] },
                  { label: 'Operations', metrics: ['ER_PUBLIC','ER_VIP','SIGNAL_WIN_RATE','AVG_LOT_CLIENT','COMM_CLIENT'] },
                ]} />
              } />
              
              <div className="p-6">
                {(() => {
                  const getColor = (value, thresholds, lowerIsBetter = true) => {
                    if (value === 0) return '#6B7280';
                    if (lowerIsBetter) {
                      if (value <= thresholds.excellent) return '#10B981';
                      if (value <= thresholds.good) return '#3B82F6';
                      if (value <= thresholds.average) return '#F59E0B';
                      return '#E63946';
                    } else {
                      if (value >= thresholds.excellent) return '#10B981';
                      if (value >= thresholds.good) return '#3B82F6';
                      if (value >= thresholds.average) return '#F59E0B';
                      return '#E63946';
                    }
                  };
                  
                  // Data sources
                  const adSpent = activeData?.total_ad_spent || 0;
                  const impressions = activeData?.total_impressions || 0;
                  const clicks = activeData?.total_clicks || 0;
                  const lpv = activeData?.total_lpv || 0;
                  const subs = activeData?.total_subscribers || 0;
                  const subsFt = activeData?.total_subs_free_trial || 0;
                  const leads = activeData?.total_leads || 0;
                  const closed = activeData?.total_closing || 0;
                  const deposit = activeData?.total_deposit || 0;
                  const commission = activeData?.total_commission || 0;
                  const activeClient = activeData?.total_active_client || 0;
                  const tradedLot = activeData?.total_traded_lot || 0;
                  const erPublic = activeData?.engagement_rate_public || 0;
                  const erVip = activeData?.engagement_rate_vip || 0;
                  const signalWinRate = activeData?.signal_win_rate || 0;
                  
                  // Marketing Cost Metrics — use KPI thresholds from admin settings
                  const km = kpiThresholds?.marketing || {};
                  const ks = kpiThresholds?.sales || {};
                  const ko = kpiThresholds?.operation || {};
                  const marketingMetrics = [
                    { name: 'CPM', value: impressions > 0 ? (adSpent / impressions) * 1000 : 0, prefix: 'RM ', thresholds: km.CPM || { excellent: 15, good: 25, average: 40 }, lowerBetter: true },
                    { name: 'CPC (Click)', value: clicks > 0 ? adSpent / clicks : 0, prefix: 'RM ', thresholds: km.CPLC || { excellent: 0.15, good: 0.30, average: 0.50 }, lowerBetter: true },
                    { name: 'CPLPV', value: lpv > 0 ? adSpent / lpv : 0, prefix: 'RM ', thresholds: km.CPLPV || { excellent: 0.20, good: 0.40, average: 0.80 }, lowerBetter: true },
                    { name: 'CPS (Subs)', value: subs > 0 ? adSpent / subs : 0, prefix: 'RM ', thresholds: km.CPS || { excellent: 10, good: 15, average: 20 }, lowerBetter: true },
                    { name: 'CPS (Free Trial)', value: subsFt > 0 ? adSpent / subsFt : 0, prefix: 'RM ', thresholds: km.CPS_FT || { excellent: 10, good: 15, average: 20 }, lowerBetter: true },
                    { name: 'CPL', value: leads > 0 ? adSpent / leads : 0, prefix: 'RM ', thresholds: km.CPL || { excellent: 70, good: 100, average: 150 }, lowerBetter: true },
                    { name: 'CPC (Client)', value: closed > 0 ? adSpent / closed : 0, prefix: 'RM ', thresholds: km.CPC_CLIENT || { excellent: 150, good: 200, average: 300 }, lowerBetter: true },
                  ];
                  
                  // Sales Performance Metrics
                  const salesMetrics = [
                    { name: 'CPC (Client)', value: closed > 0 ? adSpent / closed : 0, prefix: 'RM ', thresholds: ks.CPC_CLIENT_SALES || km.CPC_CLIENT || { excellent: 150, good: 200, average: 300 }, lowerBetter: true },
                    { name: 'Renewal Rate', value: 0, suffix: '%', thresholds: ks.RENEWAL_RATE || { excellent: 50, good: 30, average: 15 }, lowerBetter: false },
                    { name: 'Avg Deposit', value: closed > 0 ? deposit / closed : 0, prefix: '$ ', thresholds: ks.AVG_DEPOSIT || { excellent: 500, good: 300, average: 100 }, lowerBetter: false },
                    { name: 'CPL (Lead)', value: leads > 0 ? adSpent / leads : 0, prefix: 'RM ', thresholds: ks.CPL_SALES || km.CPL || { excellent: 70, good: 100, average: 150 }, lowerBetter: true },
                    { name: 'Closing Rate', value: leads > 0 ? (closed / leads) * 100 : 0, suffix: '%', thresholds: ks.CLOSING_RATE || { excellent: 40, good: 30, average: 20 }, lowerBetter: false },
                  ];
                  
                  // Operations Performance Metrics
                  const operationsMetrics = [
                    { name: 'E.R (Public)', value: erPublic, suffix: '%', thresholds: ko.ER_PUBLIC || { excellent: 8, good: 4, average: 1 }, lowerBetter: false },
                    { name: 'E.R (VIP)', value: erVip, suffix: '%', thresholds: ko.ER_VIP || { excellent: 15, good: 8, average: 3 }, lowerBetter: false },
                    { name: 'Win Rate', value: signalWinRate, suffix: '%', thresholds: ko.SIGNAL_WIN_RATE || { excellent: 80, good: 65, average: 50 }, lowerBetter: false },
                    { name: 'Avg Lot/Client', value: activeClient > 0 ? tradedLot / activeClient : 0, thresholds: ko.AVG_LOT_CLIENT || { excellent: 5, good: 2, average: 0.5 }, lowerBetter: false },
                    { name: 'Comm/Client', value: activeClient > 0 ? commission / activeClient : 0, prefix: 'RM ', thresholds: ko.COMM_CLIENT || { excellent: 500, good: 200, average: 50 }, lowerBetter: false },
                  ];
                  
                  const renderMetricCard = (metric) => {
                    const hasData = metric.value > 0;
                    const metricColor = hasData ? getColor(metric.value, metric.thresholds, metric.lowerBetter) : '#6B7280';
                    const displayValue = metric.prefix ? `${metric.prefix}${metric.value.toFixed(2)}` : 
                                        metric.suffix ? `${metric.value.toFixed(1)}${metric.suffix}` : 
                                        metric.value.toFixed(2);
                    return (
                      <div key={metric.name} className="p-2.5 rounded-lg border" style={{ backgroundColor: `${metricColor}10`, borderColor: `${metricColor}30` }}>
                        <p className="text-[10px] text-[#9CA3AF] mb-1 truncate">{metric.name}</p>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: metricColor }} />
                          <p className="text-xs sm:text-base font-mono font-bold truncate" style={{ color: metricColor }}>
                            {hasData ? displayValue : '-'}
                          </p>
                        </div>
                      </div>
                    );
                  };
                  
                  return (
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Marketing - Ads Performance */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#E63946]">
                          <div className="w-3 h-3 rounded-full bg-[#E63946]" />
                          <p className="text-sm font-bold text-[#E63946]">Ads Performance</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-2">Marketing</p>
                        <div className="grid grid-cols-2 gap-2">
                          {marketingMetrics.map(m => renderMetricCard(m))}
                        </div>
                      </div>
                      
                      {/* Vertical Separator */}
                      <div className="w-px bg-gradient-to-b from-transparent via-[hsl(var(--border))] to-transparent" />
                      
                      {/* Sales - Sales Performance */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#3B82F6]">
                          <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                          <p className="text-sm font-bold text-[#3B82F6]">Sales Performance</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-2">Sales</p>
                        <div className="grid grid-cols-2 gap-2">
                          {salesMetrics.map(m => renderMetricCard(m))}
                        </div>
                      </div>
                      
                      {/* Vertical Separator */}
                      <div className="w-px bg-gradient-to-b from-transparent via-[hsl(var(--border))] to-transparent" />
                      
                      {/* Operations - Operation Performance */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b-2 border-[#10B981]">
                          <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                          <p className="text-sm font-bold text-[#10B981]">Operation Performance</p>
                        </div>
                        <p className="text-[10px] text-center text-[#9CA3AF] mb-2">Operations</p>
                        <div className="grid grid-cols-2 gap-2">
                          {operationsMetrics.map(m => renderMetricCard(m))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* PERFORMANCE TRACKER - Uses shared component with real data */}
          <PerformanceTracker 
            metrics={performanceTrackerData}
            title="Performance Tracker"
            subtitle={`Monthly Progress • Day ${currentDay} of ${daysInMonth} • ${daysRemaining} days remaining`}
            department="marketing"
            showScrollHint={true}
            cardWidth={280}
            realDailyData={realDailyDataForTracker}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />

        </div>
      </div>

      {/* SUMMARY REPORT MODAL — dark themed, real data, month filter */}
      <ChannelSummaryReport open={showSummaryModal} onClose={() => setShowSummaryModal(false)} ib={ib} />
    </>
  );
};
